import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  ComposedChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Label,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import SpendAssessmentPanel from "./SpendAssessmentPanel";
import "./RapidAIChemicalsPage.css";

const MATURITY_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "med" },
  { label: "High", value: "high" },
];

const BUCKET_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "med" },
  { label: "High", value: "high" },
];

const VALUE_CHAIN_METRIC_OPTIONS = [
  { value: "spend", label: "Total Spend ($)", displayLabel: "Total Spend", type: "currency", color: "#ff4da6" },
  { value: "addressableSpend", label: "Addressable Spend ($)", displayLabel: "Total Addressable Spend", type: "currency", color: "#a855f7" },
  { value: "potentialSavingsAvg", label: "Potential Savings Avg ($)", displayLabel: "Potential Savings Avg", type: "currency", color: "#ec4899" },
  { value: "avgSavingsPct", label: "Avg Savings (%)", displayLabel: "Avg Savings %", type: "percent", color: "#f9a8d4" },
];

const CATEGORY_METRIC_OPTIONS = [
  { value: "spend", label: "Total Spend ($)", displayLabel: "Total Spend", type: "currency" },
  { value: "addressableSpend", label: "Addressable Spend ($)", displayLabel: "Addressable Spend", type: "currency" },
  { value: "opportunity", label: "Potential Savings Avg ($)", displayLabel: "Potential Savings Avg", type: "currency" },
  { value: "avgSavingsPct", label: "Avg Savings (%)", displayLabel: "Avg Savings %", type: "percent" },
];

const CATEGORY_COLORS = ["#ff4da6", "#f472d0", "#c084fc", "#a855f7", "#d946ef", "#ec4899", "#e879f9", "#f9a8d4"];
const REGION_COLORS = ["#ff4da6", "#d946ef", "#c084fc", "#f472d0", "#9333ea", "#ec4899", "#e879f9"];
const AXIS_TICK_STYLE = { fill: "#d8c5eb", fontSize: 12 };
const AXIS_TICK_SMALL_STYLE = { fill: "#ccb5e2", fontSize: 11 };
const AXIS_LABEL_STYLE = { fill: "#cfb8e4", fontSize: 11, fontWeight: 600 };
const CHART_GRID_STROKE = "rgba(255, 145, 219, 0.16)";
const TOOLTIP_STYLE = {
  background: "rgba(23, 9, 38, 0.96)",
  border: "1px solid rgba(255, 125, 212, 0.45)",
  borderRadius: 12,
  boxShadow: "0 12px 28px rgba(10, 4, 22, 0.55)",
};
const TOOLTIP_LABEL_STYLE = { color: "#fde8ff", fontWeight: 700 };
const TOOLTIP_ITEM_STYLE = { color: "#f9d7ff", fontSize: 12 };
const BENEFIT_MULTIPLIER_BY_BUCKET = { low: 1.5, med: 1.0, high: 0.8 };
const PROCUREMENT_MULTIPLIER_BY_BUCKET = { low: 1.5, med: 1.0, high: 0.8 };

const INITIAL_CONTROLS = {
  region: "All",
  procurementMaturity: "med",
  addressibilityFactor: "80",
  lastSourcedMonths: "12",
  pricingStartYear: "",
  pricingStartMonth: "",
  pricingEndYear: "",
  pricingEndMonth: "",
  pricingYearOptions: [],
  pricingMonthOptionsByYear: {},
};

const cleanBaseUrl = (value = "") => String(value).trim().replace(/\/+$/, "");

const buildApiUrl = (baseUrl, path = "") => {
  if (!path) return cleanBaseUrl(baseUrl);
  if (/^https?:\/\//i.test(path)) return path;

  const base = cleanBaseUrl(baseUrl);
  const cleanedPath = String(path).replace(/^\/+/, "");
  return base ? `${base}/${cleanedPath}` : `/${cleanedPath}`;
};

const extractAssessmentJobId = (path = "") => {
  const match = String(path || "").match(/\/(?:rachemical\/spend-assessment\/artifacts|rapidai\/pipeline\/artifacts)\/([^/?#]+)/i);
  return match?.[1] ? String(match[1]).trim() : "";
};

const extractFilename = (contentDisposition = "") => {
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_error) {
      return utf8Match[1];
    }
  }

  const quotedMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return quotedMatch?.[1] ? quotedMatch[1].trim() : "";
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "Rapid_Ai_Chemicals_Output.xlsx";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const decodeBase64ToBlob = (value, contentType = "application/octet-stream") => {
  const base64 = String(value || "").trim();
  if (!base64) {
    throw new Error("File payload is empty.");
  }
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType || "application/octet-stream" });
};

const parseBlobJson = async (blob) => {
  const text = await blob.text();
  return JSON.parse(text);
};

const resolveErrorMessage = async (error) => {
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    const raw = await responseData.text();
    if (!raw) return error?.message || "Request failed.";
    try {
      const parsed = JSON.parse(raw);
      return parsed?.message || parsed?.detail || parsed?.error || raw;
    } catch (_parseError) {
      return raw;
    }
  }

  if (responseData && typeof responseData === "object") {
    return (
      responseData.message ||
      responseData.detail ||
      responseData.error ||
      error?.message ||
      "Request failed."
    );
  }

  return error?.message || "Request failed.";
};

const normalizeApiPayload = (raw) => {
  let value = raw;
  // Handle double-encoded payloads like "\"{...}\"" from non-strict JSON responses.
  for (let i = 0; i < 2; i += 1) {
    if (typeof value !== "string") break;
    const text = value.trim();
    if (!text) break;
    try {
      value = JSON.parse(text);
    } catch (_parseError) {
      const relaxed = text
        .replace(/\bNaN\b/g, "null")
        .replace(/\bInfinity\b/g, "null")
        .replace(/\b-Infinity\b/g, "null");
      if (relaxed === text) break;
      try {
        value = JSON.parse(relaxed);
      } catch (_relaxedParseError) {
        break;
      }
    }
  }
  return value;
};

const extractSessionId = (payload) => {
  const data = normalizeApiPayload(payload);
  const sid = String(
    data?.session_id ||
      data?.sessionId ||
      data?.session?.id ||
      data?.dashboard?.session_id ||
      data?.dashboard?.sessionId ||
      data?.dashboard?.meta?.session_id ||
      data?.dashboard?.meta?.sessionId ||
      ""
  ).trim();
  if (sid) return { sid, data };

  if (typeof data === "string") {
    const match = data.match(/"session_id"\s*:\s*"([^"]+)"/);
    if (match?.[1]) {
      return { sid: String(match[1]).trim(), data: normalizeApiPayload(data) };
    }
  }

  return { sid: "", data };
};

const toMaturityControl = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (v.startsWith("low")) return "low";
  if (v.startsWith("high")) return "high";
  return "med";
};

const toBucketControl = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (v.startsWith("low")) return "low";
  if (v.startsWith("high")) return "high";
  return "med";
};

const toOverrideRowKey = (region, category) =>
  `${String(region || "").trim().toLowerCase().replace(/\s+/g, " ")}||${String(category || "").trim().toLowerCase()}`;

const summarizeDeepDiveField = (rows, selectors, fallback = "Unspecified") => {
  const values = [];
  rows.forEach((row) => {
    selectors.some((selector) => {
      const text = String(selector(row) ?? "").trim();
      if (!text) return false;
      if (!values.includes(text)) values.push(text);
      return true;
    });
  });
  if (!values.length) return fallback;
  return values.join(" / ");
};

const buildDeepDivePivotRows = (rows = []) => {
  const groups = new Map();
  rows.forEach((row) => {
    const category = String(row?.Category || row?.category || "").trim() || "Uncategorized";
    const groupKey = category.toLowerCase();
    const region = String(row?.Region || row?.region || "").trim();
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        row_key: groupKey,
        category,
        rows: [],
        regionRows: {},
      });
    }
    const group = groups.get(groupKey);
    group.rows.push(row);
    if (region) {
      group.regionRows[region] = row;
    }
  });

  return Array.from(groups.values())
    .map((group) => {
      const orderedRows = [...group.rows].sort(
        (left, right) => Number(right?.regional_spend ?? right?.addressable_spend ?? 0) - Number(left?.regional_spend ?? left?.addressable_spend ?? 0)
      );
      const templateRow = orderedRows[0] || {};
      return {
        row_key: group.row_key,
        category: group.category,
        l3Category: summarizeDeepDiveField(group.rows, [(row) => row?.l3_category, (row) => row?.["L3 Category"]], "Unspecified"),
        valueChain: summarizeDeepDiveField(group.rows, [(row) => row?.by_value_chain, (row) => row?.["Value Chain"]], "Unspecified"),
        byType: summarizeDeepDiveField(group.rows, [(row) => row?.by_type, (row) => row?.["L1 by type"]], "Other"),
        primaryFeedstock: summarizeDeepDiveField(
          group.rows,
          [(row) => row?.primary_feedstock, (row) => row?.["Primary Feedstock"]],
          "Unspecified"
        ),
        primaryStartingMaterial: summarizeDeepDiveField(
          group.rows,
          [(row) => row?.primary_starting_material, (row) => row?.["Primary Starting Material"]],
          "Unspecified"
        ),
        templateRow,
        regionRows: group.regionRows,
        totalRegionalSpend: group.rows.reduce((sum, row) => sum + Number(row?.regional_spend ?? 0), 0),
      };
    })
    .sort((left, right) => right.totalRegionalSpend - left.totalRegionalSpend);
};

const buildDeepDiveRegionRow = (pivotRow, region) => {
  const existingRow = pivotRow?.regionRows?.[region];
  if (existingRow) return existingRow;
  return {
    ...(pivotRow?.templateRow || {}),
    row_key: toOverrideRowKey(region, pivotRow?.category),
    Region: region,
    region,
    Category: pivotRow?.category,
    category: pivotRow?.category,
    regional_spend: 0,
    addressable_spend: 0,
    l4_category_price_change_pct: null,
    primary_starting_material_price_change_pct: null,
    primary_feedstock_price_change_pct: null,
  };
};

const DEEP_DIVE_METRIC_GROUPS = [
  { key: "regional_spend", headerLabel: "Spend", fullLabel: "Spend by region", includeTotal: true },
  { key: "addressibility_factor_percentage", headerLabel: "Addr %", fullLabel: "Addressibility Factor (%)", includeTotal: false },
  { key: "addressable_spend", headerLabel: "Addr Spend", fullLabel: "Addressibility Spend", includeTotal: true },
  { key: "top_down_savings_low_pct", headerLabel: "TD Low %", fullLabel: "Top Down Savings Low (%)", includeTotal: false },
  { key: "top_down_savings_high_pct", headerLabel: "TD High %", fullLabel: "Top Down Savings High (%)", includeTotal: false },
  { key: "last_sourced_bucket", headerLabel: "Last Src", fullLabel: "Last Sourced Bucket", includeTotal: false },
  { key: "procurement_maturity_bucket", headerLabel: "Proc. Mat.", fullLabel: "Procurement Maturity", includeTotal: false },
  { key: "final_savings_range", headerLabel: "Final Range", fullLabel: "Final Savings Range (%)", includeTotal: false },
  { key: "avg_final_savings_pct", headerLabel: "Avg Final %", fullLabel: "Avg. Final Savings (%)", includeTotal: false },
  { key: "l4_category_price_change_pct", headerLabel: "L4 Price", fullLabel: "L4 Category Price Changes (%)", includeTotal: false },
  { key: "primary_starting_material_price_change_pct", headerLabel: "Start Mat.", fullLabel: "Primary Starting Material Price Changes (%)", includeTotal: false },
  { key: "primary_feedstock_price_change_pct", headerLabel: "Feedstock", fullLabel: "Primary Feedstock Price Changes (%)", includeTotal: false },
];

const parseOptionalNonNegativeNumber = (rawValue) => {
  const text = String(rawValue ?? "").trim();
  if (!text) return null;
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric;
};

const toPercentInputValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
    return "";
  }
  const numeric = Number(rawValue);
  if (!Number.isFinite(numeric)) return "";
  return Math.abs(numeric) <= 1 ? numeric * 100.0 : numeric;
};

const getBucketMultiplier = (bucketValue, multiplierMap) => {
  const key = toBucketControl(bucketValue);
  return Number(multiplierMap[key] ?? 1);
};

const toInputNumberString = (value, decimals = 2) => {
  if (value === null || value === undefined || String(value).trim() === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return String(Number(numeric.toFixed(decimals)));
};

const formatPctFixed = (value, decimals = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${(0).toFixed(decimals)}%`;
  return `${numeric.toFixed(decimals)}%`;
};

const computeDeepDivePreview = (row, edit = {}) => {
  const baseAddressibilityPct = Number(row?.addressibility_factor_percentage ?? 0);
  const addressibilityOverride = parseOptionalNonNegativeNumber(edit?.addressibility_factor_percentage);
  const addressibilityPct = addressibilityOverride !== null ? addressibilityOverride : baseAddressibilityPct;

  const rowRegionalSpend = Number(row?.regional_spend ?? 0);
  const fallbackRegionalSpend =
    baseAddressibilityPct > 0
      ? (Number(row?.addressable_spend ?? 0) / baseAddressibilityPct) * 100.0
      : 0;
  const regionalSpend = Number.isFinite(rowRegionalSpend) && rowRegionalSpend > 0 ? rowRegionalSpend : fallbackRegionalSpend;
  const addressableSpend = regionalSpend * (Math.max(0, addressibilityPct) / 100.0);

  const baseBenefitBucket = toBucketControl(row?.last_sourced_bucket || "med");
  const baseProcurementBucket = toMaturityControl(row?.procurement_maturity_bucket || "med");
  const selectedBenefitBucket = edit?.last_sourced_bucket ? toBucketControl(edit.last_sourced_bucket) : baseBenefitBucket;
  const selectedProcurementBucket = edit?.procurement_maturity_bucket
    ? toMaturityControl(edit.procurement_maturity_bucket)
    : baseProcurementBucket;

  const currentCombinedMultiplier =
    getBucketMultiplier(baseBenefitBucket, BENEFIT_MULTIPLIER_BY_BUCKET) *
    getBucketMultiplier(baseProcurementBucket, PROCUREMENT_MULTIPLIER_BY_BUCKET);
  const safeCurrentCombined = currentCombinedMultiplier > 0 ? currentCombinedMultiplier : 1;

  const rowFinalLowPct = Number(row?.final_savings_low_pct ?? 0);
  const rowFinalHighPct = Number(row?.final_savings_high_pct ?? 0);
  const rowTopDownLowPct = Number(row?.top_down_savings_low_pct ?? row?.top_down_savings_pct ?? 0);
  const rowTopDownHighPct = Number(row?.top_down_savings_high_pct ?? row?.top_down_savings_pct ?? rowTopDownLowPct);
  const rowAvgPct = Number(row?.avg_final_savings_pct ?? 0);

  let baseSavingsLowPct = Number.isFinite(rowTopDownLowPct)
    ? rowTopDownLowPct
    : (Number.isFinite(rowFinalLowPct) ? rowFinalLowPct / safeCurrentCombined : 0);
  let baseSavingsHighPct = Number.isFinite(rowTopDownHighPct)
    ? rowTopDownHighPct
    : (Number.isFinite(rowFinalHighPct) ? rowFinalHighPct / safeCurrentCombined : baseSavingsLowPct);

  if (!Number.isFinite(baseSavingsLowPct) || baseSavingsLowPct < 0) {
    baseSavingsLowPct = 0;
  }
  if (!Number.isFinite(baseSavingsHighPct) || baseSavingsHighPct < 0) {
    baseSavingsHighPct = baseSavingsLowPct;
  }

  const topDownLowOverride = parseOptionalNonNegativeNumber(edit?.top_down_savings_low_pct);
  const topDownHighOverride = parseOptionalNonNegativeNumber(edit?.top_down_savings_high_pct);
  if (topDownLowOverride !== null) {
    baseSavingsLowPct = topDownLowOverride;
  }
  if (topDownHighOverride !== null) {
    baseSavingsHighPct = topDownHighOverride;
  }
  if (baseSavingsLowPct > baseSavingsHighPct) {
    const swappedLow = baseSavingsHighPct;
    const swappedHigh = baseSavingsLowPct;
    baseSavingsLowPct = swappedLow;
    baseSavingsHighPct = swappedHigh;
  }
  const topDownLowPct = baseSavingsLowPct;
  const topDownHighPct = baseSavingsHighPct;
  const topDownPct = (topDownLowPct + topDownHighPct) / 2.0;

  const newCombinedMultiplier =
    getBucketMultiplier(selectedBenefitBucket, BENEFIT_MULTIPLIER_BY_BUCKET) *
    getBucketMultiplier(selectedProcurementBucket, PROCUREMENT_MULTIPLIER_BY_BUCKET);

  const finalLowPct = Math.max(0, baseSavingsLowPct * newCombinedMultiplier);
  const finalHighPct = Math.max(0, baseSavingsHighPct * newCombinedMultiplier);

  const avgSavingsOverride = parseOptionalNonNegativeNumber(edit?.avg_final_savings_pct);
  const computedAvgPct = (finalLowPct + finalHighPct) / 2.0;
  const avgFinalSavingsPct = avgSavingsOverride !== null
    ? avgSavingsOverride
    : (Number.isFinite(computedAvgPct) ? computedAvgPct : (Number.isFinite(rowAvgPct) ? rowAvgPct : 0));

  return {
    addressibilityPct,
    addressableSpend,
    topDownLowPct,
    topDownHighPct,
    topDownPct,
    finalLowPct,
    finalHighPct,
    avgFinalSavingsPct,
  };
};

const buildAssessmentHierarchyFromDeepDiveRows = (rows = []) => {
  const map = {};
  rows.forEach((row) => {
    const l3Category = String(
      row?.l3_category ?? row?.l3Category ?? row?.["L3 Category"] ?? ""
    ).trim();
    const l4Category = String(
      row?.category ?? row?.Category ?? row?.["By Type - L4 Category"] ?? ""
    ).trim();
    if (!l3Category || !l4Category) return;
    if (!map[l3Category]) map[l3Category] = [];
    if (!map[l3Category].includes(l4Category)) {
      map[l3Category].push(l4Category);
    }
  });

  Object.keys(map).forEach((key) => {
    map[key] = [...map[key]].sort((left, right) => left.localeCompare(right));
  });

  return map;
};

const assessmentStatusRank = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (text === "succeeded") return 4;
  if (text === "failed" || text === "cancelled" || text === "canceled") return 3;
  if (text === "running") return 2;
  if (text === "queued") return 1;
  return 0;
};

const assessmentStatusTimestamp = (payload) => {
  const raw = String(payload?.updated_at || payload?.latest_event?.timestamp || "").trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const shouldAcceptAssessmentStatus = (currentPayload, nextPayload) => {
  if (!nextPayload) return false;
  if (!currentPayload) return true;

  const currentRank = assessmentStatusRank(currentPayload?.status);
  const nextRank = assessmentStatusRank(nextPayload?.status);
  if (currentRank === 4 && nextRank < 4) {
    return false;
  }

  const currentTs = assessmentStatusTimestamp(currentPayload);
  const nextTs = assessmentStatusTimestamp(nextPayload);
  if (currentTs && nextTs && nextTs < currentTs) {
    return false;
  }

  const currentPercent = Number(currentPayload?.percent_complete || 0);
  const nextPercent = Number(nextPayload?.percent_complete || 0);
  if ((!currentTs || !nextTs || nextTs === currentTs) && nextPercent < currentPercent && nextRank <= currentRank) {
    return false;
  }

  return true;
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "$0";
  if (Math.abs(amount) >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toFixed(0)}`;
};

const formatPct = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.0%";
  return `${num.toFixed(1)}%`;
};

const toDashboardPayload = (sessionId, controls, categoryOverrides = []) => {
  const addressibility = Number(controls.addressibilityFactor);
  const lastSourced = Number(controls.lastSourcedMonths);

  if (!Number.isFinite(addressibility) || addressibility < 0 || addressibility > 100) {
    throw new Error("Addressibility factor must be between 0 and 100.");
  }
  if (!Number.isFinite(lastSourced) || lastSourced < 0) {
    throw new Error("Benefit potential (months) must be 0 or more.");
  }

  const payload = {
    session_id: sessionId,
    region: controls.region || "All",
    procurement_maturity_bucket: controls.procurementMaturity,
    addressibility_factor_percentage: addressibility,
    last_sourced_months: lastSourced,
  };
  const pricingStartYearText = String(controls.pricingStartYear || "").trim();
  const pricingStartMonthText = String(controls.pricingStartMonth || "").trim();
  const pricingEndYearText = String(controls.pricingEndYear || "").trim();
  const pricingEndMonthText = String(controls.pricingEndMonth || "").trim();

  if (pricingStartYearText && pricingStartMonthText) {
    const pricingStartYear = Number(pricingStartYearText);
    const pricingStartMonth = Number(pricingStartMonthText);
    if (Number.isFinite(pricingStartYear) && Number.isFinite(pricingStartMonth)) {
      payload.pricing_start_year = Math.trunc(pricingStartYear);
      payload.pricing_start_month = Math.trunc(pricingStartMonth);
    }
  }
  if (pricingEndYearText && pricingEndMonthText) {
    const pricingEndYear = Number(pricingEndYearText);
    const pricingEndMonth = Number(pricingEndMonthText);
    if (Number.isFinite(pricingEndYear) && Number.isFinite(pricingEndMonth)) {
      payload.pricing_end_year = Math.trunc(pricingEndYear);
      payload.pricing_end_month = Math.trunc(pricingEndMonth);
    }
  }
  if (Array.isArray(categoryOverrides) && categoryOverrides.length) {
    payload.category_overrides = categoryOverrides;
  }
  return payload;
};

export default function RapidAIChemicalsPage() {
  const apiBase = useMemo(() => "https://webappaichemie-cqg3b7aqetdpfygs.canadacentral-01.azurewebsites.net", []);

  const [view, setView] = useState("home");
  const [selectedSpendFile, setSelectedSpendFile] = useState(null);
  const [selectedPricingFile, setSelectedPricingFile] = useState(null);
  const [categorizationSpendFile, setCategorizationSpendFile] = useState(null);
  const [categorizationMappingFile, setCategorizationMappingFile] = useState(null);
  const [categorizationRun, setCategorizationRun] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [controls, setControls] = useState(INITIAL_CONTROLS);
  const [draftControls, setDraftControls] = useState(INITIAL_CONTROLS);
  const [isControlsModalOpen, setIsControlsModalOpen] = useState(false);
  const [activeDashboardTab, setActiveDashboardTab] = useState("overview");
  const [regionOptions, setRegionOptions] = useState(["All"]);
  const [dashboard, setDashboard] = useState(null);
  const [deepDiveEdits, setDeepDiveEdits] = useState({});
  const [valueChainMetric, setValueChainMetric] = useState("spend");
  const [l4CategoryMetric, setL4CategoryMetric] = useState("spend");
  const [l3CategoryMetric, setL3CategoryMetric] = useState("spend");
  const [selectedValueChain, setSelectedValueChain] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedL3Categories, setSelectedL3Categories] = useState(["All"]);
  const [isL3DropdownOpen, setIsL3DropdownOpen] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState(["All"]);
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const [supplierPage, setSupplierPage] = useState(0);
  const [supplierPerPage, setSupplierPerPage] = useState(50);
  const [isSidePaneCollapsed, setIsSidePaneCollapsed] = useState(false);
  const [deepDiveCategorySearch, setDeepDiveCategorySearch] = useState("");
  const [deepDiveValueChainSearch, setDeepDiveValueChainSearch] = useState("");
  const [assessmentCategories, setAssessmentCategories] = useState([]);
  const [assessmentCategoryHierarchy, setAssessmentCategoryHierarchy] = useState({});
  const [assessmentSelectedL3, setAssessmentSelectedL3] = useState("");
  const [assessmentSelectedL4, setAssessmentSelectedL4] = useState("");
  const [assessmentRunMeta, setAssessmentRunMeta] = useState(null);
  const [assessmentStatusPayload, setAssessmentStatusPayload] = useState(null);
  const [assessmentArtifacts, setAssessmentArtifacts] = useState(null);
  const [assessmentError, setAssessmentError] = useState("");
  const [isAssessmentCategoriesLoading, setIsAssessmentCategoriesLoading] = useState(false);
  const [isAssessmentRunning, setIsAssessmentRunning] = useState(false);
  const [isAssessmentArtifactsLoading, setIsAssessmentArtifactsLoading] = useState(false);
  const [assessmentDownloadingArtifact, setAssessmentDownloadingArtifact] = useState("");

  const [isInitializing, setIsInitializing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [categorizationErrorMessage, setCategorizationErrorMessage] = useState("");
  const [isCategorizationSubmitting, setIsCategorizationSubmitting] = useState(false);
  const [isCategorizationPolling, setIsCategorizationPolling] = useState(false);
  const [categorizationDownloadingArtifact, setCategorizationDownloadingArtifact] = useState("");
  const l3DropdownRef = useRef(null);
  const supplierDropdownRef = useRef(null);
  const assessmentStatusRef = useRef(null);
  const assessmentScopeVersionRef = useRef(0);

  const dashboardInitUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/dashboard/init"), [apiBase]);
  const dashboardComputeUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/dashboard/compute"), [apiBase]);
  const dashboardExportUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/dashboard/export?return_file=true"), [apiBase]);
  const categorizationCreateUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/categorization/runs"), [apiBase]);
  const assessmentCategoriesUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/spend-assessment/categories"), [apiBase]);
  const assessmentRunUrl = useMemo(() => buildApiUrl(apiBase, "/rachemical/spend-assessment/run"), [apiBase]);
  const categorizationRequestId = String(categorizationRun?.request_id || "").trim();
  const categorizationStatusUrl = useMemo(
    () => (categorizationRequestId ? buildApiUrl(apiBase, `/rachemical/categorization/runs/${categorizationRequestId}`) : ""),
    [apiBase, categorizationRequestId]
  );

  const applyDashboardFromResponse = (data) => {
    const nextDashboard = data?.dashboard || null;
    const nextControls = data?.controls || nextDashboard?.controls || {};

    setDashboard(nextDashboard);

    const options = Array.isArray(nextControls.region_options) && nextControls.region_options.length
      ? nextControls.region_options
      : ["All"];
    setRegionOptions(options);

    setControls((current) => {
      const maturityValue = toMaturityControl(nextControls.procurement_maturity_bucket || current.procurementMaturity);
      const addrValue = nextControls.addressibility_factor_percentage ?? current.addressibilityFactor;
      const lastValue = nextControls.last_sourced_months ?? current.lastSourcedMonths;

      const yearOptions = Array.isArray(nextControls.pricing_year_options)
        ? nextControls.pricing_year_options
          .map((year) => String(year ?? "").trim())
          .filter(Boolean)
        : (Array.isArray(current.pricingYearOptions) ? current.pricingYearOptions : []);

      const monthOptionsRaw = (
        nextControls.pricing_month_options_by_year &&
        typeof nextControls.pricing_month_options_by_year === "object"
      )
        ? nextControls.pricing_month_options_by_year
        : current.pricingMonthOptionsByYear;
      const monthOptionsByYear = {};
      Object.entries(monthOptionsRaw || {}).forEach(([yearKey, rows]) => {
        const cleanYearKey = String(yearKey || "").trim();
        if (!cleanYearKey) return;
        const normalizedRows = (Array.isArray(rows) ? rows : [])
          .map((row) => {
            const monthIndex = Number(row?.month_index);
            if (!Number.isFinite(monthIndex) || monthIndex < 1 || monthIndex > 12) {
              return null;
            }
            const monthIndexText = String(Math.trunc(monthIndex));
            const monthLabel = String(row?.month_label || row?.label || monthIndexText).trim() || monthIndexText;
            return {
              month_index: monthIndexText,
              month_label: monthLabel,
            };
          })
          .filter(Boolean);
        if (normalizedRows.length) {
          monthOptionsByYear[cleanYearKey] = normalizedRows;
        }
      });

      const startYearCandidate = String(nextControls.pricing_start_year ?? current.pricingStartYear ?? "").trim();
      const endYearCandidate = String(nextControls.pricing_end_year ?? current.pricingEndYear ?? "").trim();
      const startYearValue = yearOptions.includes(startYearCandidate)
        ? startYearCandidate
        : (yearOptions[0] || "");
      const endYearValue = yearOptions.includes(endYearCandidate)
        ? endYearCandidate
        : (yearOptions[yearOptions.length - 1] || startYearValue || "");

      const startMonthOptions = monthOptionsByYear[startYearValue] || [];
      const endMonthOptions = monthOptionsByYear[endYearValue] || [];
      const startMonthCandidate = String(nextControls.pricing_start_month_index ?? current.pricingStartMonth ?? "").trim();
      const endMonthCandidate = String(nextControls.pricing_end_month_index ?? current.pricingEndMonth ?? "").trim();
      const startMonthValue = startMonthOptions.some((row) => row.month_index === startMonthCandidate)
        ? startMonthCandidate
        : (startMonthOptions[0]?.month_index || "");
      const endMonthValue = endMonthOptions.some((row) => row.month_index === endMonthCandidate)
        ? endMonthCandidate
        : (endMonthOptions[endMonthOptions.length - 1]?.month_index || "");

      return {
        region: "All",
        procurementMaturity: maturityValue,
        addressibilityFactor: String(addrValue),
        lastSourcedMonths: String(lastValue),
        pricingStartYear: startYearValue,
        pricingStartMonth: startMonthValue,
        pricingEndYear: endYearValue,
        pricingEndMonth: endMonthValue,
        pricingYearOptions: yearOptions,
        pricingMonthOptionsByYear: monthOptionsByYear,
      };
    });

    if (Array.isArray(nextControls.category_overrides)) {
      const mapped = {};
      nextControls.category_overrides.forEach((row) => {
        const region = String(row?.region || "").trim();
        const category = String(row?.category || "").trim();
        if (!region || !category) return;
        const key = toOverrideRowKey(region, category);
        mapped[key] = {
          row_key: key,
          region,
          category,
          addressibility_factor_percentage: toInputNumberString(
            toPercentInputValue(
              row.addressibility_factor_percentage ?? row.addressability_factor_percentage ?? row.addressability_factor
            )
          ),
          top_down_savings_low_pct:
            toInputNumberString(
              toPercentInputValue(row.top_down_savings_low_pct ?? row.top_down_savings_pct)
            ),
          top_down_savings_high_pct:
            toInputNumberString(
              toPercentInputValue(row.top_down_savings_high_pct ?? row.top_down_savings_pct)
            ),
          last_sourced_bucket: toBucketControl(row.last_sourced_bucket || row.benefit_bucket || ""),
          procurement_maturity_bucket: toMaturityControl(row.procurement_maturity_bucket || row.procurement_maturity || ""),
          avg_final_savings_pct: toInputNumberString(
            toPercentInputValue(row.avg_final_savings_pct ?? row.avg_savings_pct)
          ),
        };
      });
      setDeepDiveEdits(mapped);
    }

    const pageSize = Number(nextDashboard?.supplier_breakdown?.page_size);
    if (Number.isFinite(pageSize) && pageSize > 0) {
      setSupplierPerPage(pageSize);
    }
    setSupplierPage(0);
  };

  const clearAssessmentRunState = useCallback(({ preserveError = false } = {}) => {
    assessmentScopeVersionRef.current += 1;
    assessmentStatusRef.current = null;
    setAssessmentRunMeta(null);
    setAssessmentStatusPayload(null);
    setAssessmentArtifacts(null);
    if (!preserveError) {
      setAssessmentError("");
    }
    setIsAssessmentRunning(false);
    setIsAssessmentArtifactsLoading(false);
    setAssessmentDownloadingArtifact("");
  }, []);

  const resetAssessmentState = useCallback(() => {
    assessmentStatusRef.current = null;
    assessmentScopeVersionRef.current += 1;
    setAssessmentCategories([]);
    setAssessmentCategoryHierarchy({});
    setAssessmentSelectedL3("");
    setAssessmentSelectedL4("");
    setAssessmentRunMeta(null);
    setAssessmentStatusPayload(null);
    setAssessmentArtifacts(null);
    setAssessmentError("");
    setIsAssessmentCategoriesLoading(false);
    setIsAssessmentRunning(false);
    setIsAssessmentArtifactsLoading(false);
    setAssessmentDownloadingArtifact("");
  }, []);

  const handleAssessmentSelectL3 = useCallback((nextValue) => {
    const normalized = String(nextValue || "").trim();
    if (normalized === assessmentSelectedL3) {
      return;
    }
    clearAssessmentRunState();
    setAssessmentSelectedL3(normalized);
    setAssessmentSelectedL4("");
  }, [assessmentSelectedL3, clearAssessmentRunState]);

  const handleAssessmentSelectL4 = useCallback((nextValue) => {
    const normalized = String(nextValue || "").trim();
    if (normalized === assessmentSelectedL4) {
      return;
    }
    clearAssessmentRunState();
    setAssessmentSelectedL4(normalized);
  }, [assessmentSelectedL4, clearAssessmentRunState]);

  const buildCategoryOverridePayload = useCallback(() => {
    const rows = Object.values(deepDiveEdits || {});
    if (!rows.length) return [];

    const overrides = [];
    rows.forEach((row) => {
      const region = String(row?.region || "").trim();
      const category = String(row?.category || "").trim();
      if (!region || !category) return;

      const out = { region, category };

      const parseOptionalNumber = (raw) => {
        const text = String(raw ?? "").trim();
        if (!text) return null;
        const numeric = Number(text);
        if (!Number.isFinite(numeric) || numeric < 0) {
          throw new Error("Deep dive overrides must be numeric values >= 0.");
        }
        return numeric;
      };

      const addressibility = parseOptionalNumber(row.addressibility_factor_percentage);
      if (addressibility !== null) out.addressibility_factor_percentage = addressibility;

      const topDownLow = parseOptionalNumber(row.top_down_savings_low_pct);
      const topDownHigh = parseOptionalNumber(row.top_down_savings_high_pct);
      if (topDownLow !== null && topDownHigh !== null && topDownLow > topDownHigh) {
        throw new Error("Top down savings low end must be less than or equal to high end.");
      }
      if (topDownLow !== null) out.top_down_savings_low_pct = topDownLow;
      if (topDownHigh !== null) out.top_down_savings_high_pct = topDownHigh;
      if (topDownLow !== null && topDownHigh !== null) {
        out.top_down_savings_pct = (topDownLow + topDownHigh) / 2.0;
      }

      const avgFinal = parseOptionalNumber(row.avg_final_savings_pct);
      if (avgFinal !== null) out.avg_final_savings_pct = avgFinal;

      const lastBucket = String(row.last_sourced_bucket || "").trim();
      if (lastBucket) out.last_sourced_bucket = lastBucket;

      const procurement = String(row.procurement_maturity_bucket || "").trim();
      if (procurement) out.procurement_maturity_bucket = procurement;

      if (Object.keys(out).length > 2) {
        overrides.push(out);
      }
    });

    return overrides;
  }, [deepDiveEdits]);

  const refreshDashboard = useCallback(
    async (nextControls, { silent = false } = {}) => {
      if (!sessionId) return;

      let payload;
      try {
        payload = toDashboardPayload(sessionId, nextControls, buildCategoryOverridePayload());
      } catch (validationError) {
        if (!silent) {
          setErrorMessage(validationError.message || "Invalid dashboard controls.");
        }
        return;
      }

      setIsRefreshing(true);
      try {
        const response = await axios.post(dashboardComputeUrl, payload, { timeout: 180000 });
        const data = normalizeApiPayload(response?.data);
        setDashboard(data?.dashboard || null);
        setErrorMessage("");
      } catch (error) {
        const message = await resolveErrorMessage(error);
        setErrorMessage(message);
      } finally {
        setIsRefreshing(false);
      }
    },
    [buildCategoryOverridePayload, dashboardComputeUrl, sessionId]
  );

  useEffect(() => {
    setSupplierPage(0);
  }, [supplierPerPage]);

  useEffect(() => {
    if (!isControlsModalOpen) {
      setDraftControls(controls);
    }
  }, [controls, isControlsModalOpen]);

  useEffect(() => {
    if (!isControlsModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsControlsModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isControlsModalOpen]);

  useEffect(() => {
    const status = String(categorizationRun?.status || "").trim().toLowerCase();
    if (view !== "categorization" || !categorizationRequestId || !categorizationStatusUrl) {
      return undefined;
    }
    if (!["queued", "running", "unknown"].includes(status)) {
      return undefined;
    }

    let isCancelled = false;

    const pollRun = async () => {
      setIsCategorizationPolling(true);
      try {
        const response = await axios.get(categorizationStatusUrl, { timeout: 180000 });
        if (isCancelled) return;
        setCategorizationRun(response?.data || null);
        setCategorizationErrorMessage("");
      } catch (error) {
        if (isCancelled) return;
        const message = await resolveErrorMessage(error);
        setCategorizationErrorMessage(message);
      } finally {
        if (!isCancelled) {
          setIsCategorizationPolling(false);
        }
      }
    };

    pollRun();
    const intervalId = window.setInterval(pollRun, 5000);
    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [categorizationRequestId, categorizationRun?.status, categorizationStatusUrl, view]);

  const resetCategorizationFlow = () => {
    setCategorizationSpendFile(null);
    setCategorizationMappingFile(null);
    setCategorizationRun(null);
    setCategorizationErrorMessage("");
    setIsCategorizationSubmitting(false);
    setIsCategorizationPolling(false);
    setCategorizationDownloadingArtifact("");
  };

  const handleOpenCategorization = () => {
    resetCategorizationFlow();
    setView("categorization");
  };

  const handleBackFromCategorization = () => {
    resetCategorizationFlow();
    setView("home");
  };

  const handleCategorizationSpendFileSelect = (event) => {
    const file = event.target.files?.[0];
    setCategorizationSpendFile(file || null);
    setCategorizationErrorMessage("");
  };

  const handleCategorizationMappingFileSelect = (event) => {
    const file = event.target.files?.[0];
    setCategorizationMappingFile(file || null);
    setCategorizationErrorMessage("");
  };

  const handleStartCategorization = async () => {
    if (!categorizationSpendFile) {
      setCategorizationErrorMessage("Upload a spend workbook before starting categorization.");
      return;
    }
    if (!categorizationMappingFile) {
      setCategorizationErrorMessage("Upload a molecular mapping workbook before starting categorization.");
      return;
    }

    setIsCategorizationSubmitting(true);
    setCategorizationErrorMessage("");

    try {
      const formData = new FormData();
      formData.append("spend_file", categorizationSpendFile);
      formData.append("molecular_mapping_file", categorizationMappingFile);
      const response = await axios.post(categorizationCreateUrl, formData, { timeout: 300000 });
      setCategorizationRun(response?.data || null);
    } catch (error) {
      const message = await resolveErrorMessage(error);
      setCategorizationErrorMessage(message);
    } finally {
      setIsCategorizationSubmitting(false);
    }
  };

  const handleCategorizationDownload = async (artifactKey) => {
    if (!categorizationRequestId) return;

    setCategorizationDownloadingArtifact(artifactKey);
    setCategorizationErrorMessage("");
    try {
      const downloadUrl = buildApiUrl(
        apiBase,
        `/rachemical/categorization/runs/${categorizationRequestId}/download/${artifactKey}`
      );
      const response = await axios.get(downloadUrl, {
        responseType: "blob",
        timeout: 300000,
      });
      const contentType = String(response.headers?.["content-type"] || "").toLowerCase();
      if (contentType.includes("application/json")) {
        const parsed = await parseBlobJson(response.data);
        throw new Error(parsed?.error || "Download did not return a file.");
      }
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const fallbackName = categorizationRun?.outputs?.[artifactKey]?.filename || `${artifactKey}_${categorizationRequestId}.xlsx`;
      const name = extractFilename(contentDisposition) || fallbackName;
      triggerDownload(response.data, name);
    } catch (error) {
      const message = await resolveErrorMessage(error);
      setCategorizationErrorMessage(message);
    } finally {
      setCategorizationDownloadingArtifact("");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    setSelectedSpendFile(file || null);
    setErrorMessage("");
  };

  const handlePricingFileSelect = (event) => {
    const file = event.target.files?.[0];
    setSelectedPricingFile(file || null);
    setErrorMessage("");
  };

  const handleProceedToDashboard = async () => {
    if (!selectedSpendFile) {
      setErrorMessage("Upload a spend sheet before proceeding.");
      return;
    }
    if (!selectedPricingFile) {
      setErrorMessage("Upload a pricing sheet before proceeding.");
      return;
    }

    setIsInitializing(true);
    setErrorMessage("");

    try {
      const formData = new FormData();
      formData.append("file", selectedSpendFile);
      formData.append("pricing_file", selectedPricingFile);
      formData.append("region", "All");
      formData.append("procurement_maturity_bucket", controls.procurementMaturity);
      formData.append("addressibility_factor_percentage", controls.addressibilityFactor);
      formData.append("last_sourced_months", controls.lastSourcedMonths);

      const response = await axios.post(dashboardInitUrl, formData, { timeout: 300000 });
      const { sid, data } = extractSessionId(response?.data || {});
      if (!sid) {
        throw new Error(`Session was not created by backend. ${JSON.stringify(data)}`);
      }

      resetAssessmentState();
      setSessionId(sid);
      applyDashboardFromResponse(data);
      setView("dashboard");
      setErrorMessage("");
    } catch (error) {
      const message = await resolveErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleOpenControlsModal = () => {
    setDraftControls(controls);
    setIsControlsModalOpen(true);
  };

  const handleCloseControlsModal = () => {
    setIsControlsModalOpen(false);
    setDraftControls(controls);
  };

  const handleDraftControlChange = (field) => (event) => {
    const value = event.target.value;
    setDraftControls((current) => ({ ...current, [field]: value }));
  };

  const handleApplyControls = async () => {
    const nextControls = { ...draftControls, region: "All" };

    try {
      toDashboardPayload(sessionId || "local-session", nextControls, buildCategoryOverridePayload());
      setErrorMessage("");
      setControls(nextControls);
      setIsControlsModalOpen(false);
      await refreshDashboard(nextControls, { silent: false });
    } catch (error) {
      setErrorMessage(error?.message || "Invalid dashboard controls.");
    }
  };

  const handleExport = async () => {
    if (!sessionId) return;

    let payload;
    try {
      payload = toDashboardPayload(sessionId, controls, buildCategoryOverridePayload());
    } catch (validationError) {
      setErrorMessage(validationError.message || "Invalid dashboard controls.");
      return;
    }

    setIsExporting(true);
    setErrorMessage("");

    try {
      const response = await axios.post(dashboardExportUrl, payload, {
        responseType: "blob",
        timeout: 300000,
      });
      const contentType = String(response.headers?.["content-type"] || "").toLowerCase();
      if (contentType.includes("application/json")) {
        const parsed = await parseBlobJson(response.data);
        throw new Error(parsed?.error || "Export did not return a file.");
      }
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const name = extractFilename(contentDisposition) || "Rapid_AI_Chemicals_Output.xlsx";
      triggerDownload(response.data, name);
    } catch (error) {
      const message = await resolveErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNewFile = () => {
    resetCategorizationFlow();
    resetAssessmentState();
    setView("home");
    setSelectedSpendFile(null);
    setSelectedPricingFile(null);
    setSessionId("");
    setControls(INITIAL_CONTROLS);
    setDraftControls(INITIAL_CONTROLS);
    setIsControlsModalOpen(false);
    setActiveDashboardTab("overview");
    setRegionOptions(["All"]);
    setDashboard(null);
    setDeepDiveEdits({});
    setValueChainMetric("spend");
    setL4CategoryMetric("spend");
    setL3CategoryMetric("spend");
    setSelectedValueChain("All");
    setSelectedCategory("All");
    setSelectedL3Categories(["All"]);
    setIsL3DropdownOpen(false);
    setSelectedSuppliers(["All"]);
    setIsSupplierDropdownOpen(false);
    setSupplierPage(0);
    setSupplierPerPage(50);
    setIsSidePaneCollapsed(false);
    setDeepDiveCategorySearch("");
    setDeepDiveValueChainSearch("");
    setIsInitializing(false);
    setIsRefreshing(false);
    setIsExporting(false);
    setErrorMessage("");
  };

  const pricingYearOptions = Array.isArray(controls.pricingYearOptions)
    ? controls.pricingYearOptions
    : [];
  const pricingMonthOptionsByYear = (
    controls.pricingMonthOptionsByYear &&
    typeof controls.pricingMonthOptionsByYear === "object"
  )
    ? controls.pricingMonthOptionsByYear
    : {};
  const pricingStartMonthOptions = pricingMonthOptionsByYear[String(controls.pricingStartYear || "").trim()] || [];
  const pricingEndMonthOptions = pricingMonthOptionsByYear[String(controls.pricingEndYear || "").trim()] || [];
  const showPricingRangeControls = activeDashboardTab === "deepdive" && pricingYearOptions.length > 0;

  const handlePricingRangeControlChange = (field) => async (event) => {
    const rawValue = String(event.target.value || "").trim();
    const nextControls = { ...controls, [field]: rawValue };

    if (field === "pricingStartYear") {
      const months = pricingMonthOptionsByYear[rawValue] || [];
      if (!months.some((row) => row.month_index === String(nextControls.pricingStartMonth || "").trim())) {
        nextControls.pricingStartMonth = months[0]?.month_index || "";
      }
    } else if (field === "pricingEndYear") {
      const months = pricingMonthOptionsByYear[rawValue] || [];
      if (!months.some((row) => row.month_index === String(nextControls.pricingEndMonth || "").trim())) {
        nextControls.pricingEndMonth = months[months.length - 1]?.month_index || "";
      }
    }

    setControls(nextControls);
    setDraftControls(nextControls);
    setErrorMessage("");
    await refreshDashboard(nextControls, { silent: false });
  };

  const summary = dashboard?.summary || {};
  const potentialSavingsAvg = summary.potential_savings_avg ?? summary.total_opportunity_spend ?? 0;
  const categoryRows = useMemo(() => dashboard?.category_breakdown?.rows || [], [dashboard]);
  const categoryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        categoryRows
          .map((row) => String(row.Category || "Uncategorized").trim())
          .filter(Boolean)
      )
    );
    return ["All", ...unique];
  }, [categoryRows]);
  const categoryRowsFiltered = selectedCategory === "All"
    ? categoryRows
    : categoryRows.filter((row) => String(row.Category || "Uncategorized").trim() === selectedCategory);
  const categoryChartData = categoryRowsFiltered.map((row) => ({
    name: row.Category || "Uncategorized",
    spend: Number(row.spend || 0),
    addressableSpend: Number(row.addressable_spend || 0),
    opportunity: Number(row.opportunity_spend || 0),
    avgSavingsPct:
      Number(row.addressable_spend || 0) > 0
        ? (Number(row.opportunity_spend || 0) / Number(row.addressable_spend || 0)) * 100.0
        : 0,
    suppliers: Number(row.suppliers || 0),
  }));
  const categoryChartWidth = Math.max(1200, categoryChartData.length * 80);
  const l3CategoryRows = useMemo(() => dashboard?.l3_category_breakdown?.rows || [], [dashboard]);
  const l3CategoryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        l3CategoryRows
          .map((row) => String(row["L3 Category"] || "Unspecified").trim())
          .filter(Boolean)
      )
    );
    return ["All", ...unique];
  }, [l3CategoryRows]);
  const isAllL3CategoriesSelected = selectedL3Categories.includes("All");
  const l3CategoryRowsFiltered = isAllL3CategoriesSelected
    ? l3CategoryRows
    : l3CategoryRows.filter((row) => selectedL3Categories.includes(String(row["L3 Category"] || "Unspecified").trim()));
  const l3CategoryChartData = l3CategoryRowsFiltered.map((row) => ({
    name: row["L3 Category"] || "Unspecified",
    spend: Number(row.spend || 0),
    addressableSpend: Number(row.addressable_spend || 0),
    opportunity: Number(row.opportunity_spend || 0),
    avgSavingsPct:
      Number(row.addressable_spend || 0) > 0
        ? (Number(row.opportunity_spend || 0) / Number(row.addressable_spend || 0)) * 100.0
        : 0,
    suppliers: Number(row.suppliers || 0),
  }));
  const l3CategoryChartWidth = Math.max(1100, l3CategoryChartData.length * 78);
  const l3ChartShouldScroll = l3CategoryChartData.length > 14;
  const l3CategorySelectedLabel = isAllL3CategoriesSelected
    ? "All L3 categories"
    : (selectedL3Categories.length <= 2
      ? selectedL3Categories.join(", ")
      : `${selectedL3Categories.length} selected`);

  const regionalRows = (dashboard?.regional_distribution || []).map((row) => ({
    name: row.Region || "Unknown",
    spend: Number(row.spend || 0),
    share: Number(row.share_pct || 0),
  }));

  const valueChainRows = useMemo(() => dashboard?.value_chain_breakdown?.rows || [], [dashboard]);
  const valueChainOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        valueChainRows
          .map((row) => String(row["Value Chain"] || "Unspecified").trim())
          .filter(Boolean)
      )
    );
    return ["All", ...unique];
  }, [valueChainRows]);
  const valueChainRowsFiltered = selectedValueChain === "All"
    ? valueChainRows
    : valueChainRows.filter((row) => String(row["Value Chain"] || "Unspecified").trim() === selectedValueChain);
  const valueChainChartData = valueChainRowsFiltered.map((row) => ({
    name: row["Value Chain"] || "Unspecified",
    spend: Number(row.spend || 0),
    addressableSpend: Number(row.addressable_spend || 0),
    potentialSavingsAvg: Number(row.potential_savings_avg || 0),
    avgSavingsPct: Number(row.avg_savings_pct || 0),
    suppliers: Number(row.suppliers || 0),
  }));
  const selectedL4CategoryMetric =
    CATEGORY_METRIC_OPTIONS.find((option) => option.value === l4CategoryMetric) || CATEGORY_METRIC_OPTIONS[0];
  const selectedL3CategoryMetric =
    CATEGORY_METRIC_OPTIONS.find((option) => option.value === l3CategoryMetric) || CATEGORY_METRIC_OPTIONS[0];
  const selectedValueChainMetric =
    VALUE_CHAIN_METRIC_OPTIONS.find((option) => option.value === valueChainMetric) || VALUE_CHAIN_METRIC_OPTIONS[0];
  const isAllValueChainsSelected = selectedValueChain === "All";

  useEffect(() => {
    if (!valueChainOptions.includes(selectedValueChain)) {
      setSelectedValueChain("All");
    }
  }, [valueChainOptions, selectedValueChain]);

  const supplierRows = useMemo(() => dashboard?.supplier_breakdown?.rows || [], [dashboard]);
  const supplierOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        supplierRows
          .map((row) => String(row.Supplier || row.supplier || "Unknown").trim())
          .filter(Boolean)
      )
    );
    return ["All", ...unique];
  }, [supplierRows]);
  const isAllSuppliersSelected = selectedSuppliers.includes("All");
  const supplierRowsFiltered = isAllSuppliersSelected
    ? supplierRows
    : supplierRows.filter((row) => selectedSuppliers.includes(String(row.Supplier || row.supplier || "Unknown").trim()));
  const totalSupplierPages = Math.max(1, Math.ceil(supplierRowsFiltered.length / supplierPerPage));
  const safeSupplierPage = Math.min(supplierPage, totalSupplierPages - 1);
  const supplierSliceStart = safeSupplierPage * supplierPerPage;
  const supplierSliceEnd = supplierSliceStart + supplierPerPage;
  const supplierPageRows = supplierRowsFiltered.slice(supplierSliceStart, supplierSliceEnd).map((row) => ({
    name: row.Supplier || row.supplier || "Unknown",
    spend: Number(row.spend || 0),
    categories: Number(row.categories || 0),
  }));
  const supplierSelectedLabel = isAllSuppliersSelected
    ? "All suppliers"
    : (selectedSuppliers.length <= 2
      ? selectedSuppliers.join(", ")
      : `${selectedSuppliers.length} selected`);

  const categoryTopCount = Number(dashboard?.category_breakdown?.top_80_count || 0);
  const categoryTailCount = Number(dashboard?.category_breakdown?.tail_count || 0);
  const l3CategoryTopCount = Number(dashboard?.l3_category_breakdown?.top_80_count || 0);
  const l3CategoryTailCount = Number(dashboard?.l3_category_breakdown?.tail_count || 0);
  const valueChainTopCount = Number(dashboard?.value_chain_breakdown?.top_80_count || 0);
  const valueChainTailCount = Number(dashboard?.value_chain_breakdown?.tail_count || 0);
  const supplierTopCount = Number(dashboard?.supplier_breakdown?.top_80_count || 0);
  const supplierTailCount = Number(dashboard?.supplier_breakdown?.tail_count || 0);
  const isAllCategoriesSelected = selectedCategory === "All";

  const showDashboard = view === "dashboard";
  const showCategorization = view === "categorization";
  const categorizationStatus = String(categorizationRun?.status || "").trim().toLowerCase();
  const categorizationResult = categorizationRun?.result || {};
  const dashboardHeading = activeDashboardTab === "deepdive"
    ? "Category Input"
    : (activeDashboardTab === "assessment" ? "Spend Assessment" : "Savings Dashboard");

  useEffect(() => {
    if (!categoryOptions.includes(selectedCategory)) {
      setSelectedCategory("All");
    }
  }, [categoryOptions, selectedCategory]);

  useEffect(() => {
    if (!selectedL3Categories.length || selectedL3Categories.includes("All")) {
      return;
    }
    const valid = selectedL3Categories.filter((option) => l3CategoryOptions.includes(option));
    if (!valid.length) {
      setSelectedL3Categories(["All"]);
      return;
    }
    if (valid.length !== selectedL3Categories.length) {
      setSelectedL3Categories(valid);
    }
  }, [l3CategoryOptions, selectedL3Categories]);

  useEffect(() => {
    if (!selectedSuppliers.length || selectedSuppliers.includes("All")) {
      return;
    }
    const valid = selectedSuppliers.filter((option) => supplierOptions.includes(option));
    if (!valid.length) {
      setSelectedSuppliers(["All"]);
      return;
    }
    if (valid.length !== selectedSuppliers.length) {
      setSelectedSuppliers(valid);
    }
  }, [selectedSuppliers, supplierOptions]);

  useEffect(() => {
    if (!isL3DropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (l3DropdownRef.current && !l3DropdownRef.current.contains(event.target)) {
        setIsL3DropdownOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [isL3DropdownOpen]);

  useEffect(() => {
    if (!isSupplierDropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setIsSupplierDropdownOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [isSupplierDropdownOpen]);

  useEffect(() => {
    setSupplierPage(0);
  }, [selectedSuppliers, supplierPerPage]);

  const deepDiveRows = useMemo(() => dashboard?.category_deep_dive?.rows || [], [dashboard]);
  const assessmentHierarchyFallback = useMemo(
    () => buildAssessmentHierarchyFromDeepDiveRows(deepDiveRows),
    [deepDiveRows]
  );
  const deepDiveRegionColumns = useMemo(
    () => regionOptions.filter((option) => String(option || "").trim().toLowerCase() !== "all"),
    [regionOptions]
  );
  const deepDivePivotRows = useMemo(() => buildDeepDivePivotRows(deepDiveRows), [deepDiveRows]);
  const deepDiveEditedCount = useMemo(() => Object.keys(deepDiveEdits || {}).length, [deepDiveEdits]);
  const deepDiveFilteredRows = useMemo(() => {
    const categoryQuery = String(deepDiveCategorySearch || "").trim().toLowerCase();
    const valueChainQuery = String(deepDiveValueChainSearch || "").trim().toLowerCase();
    return deepDivePivotRows.filter((row) => {
      const category = String(row?.category || "").toLowerCase();
      const valueChain = String(row?.valueChain || "").toLowerCase();
      const categoryMatch = !categoryQuery || category.includes(categoryQuery);
      const valueChainMatch = !valueChainQuery || valueChain.includes(valueChainQuery);
      return categoryMatch && valueChainMatch;
    });
  }, [deepDiveCategorySearch, deepDivePivotRows, deepDiveValueChainSearch]);

  useEffect(() => {
    if (!deepDivePivotRows.length) {
      setDeepDiveEdits({});
      return;
    }
    const validKeys = new Set();
    deepDivePivotRows.forEach((row) => {
      deepDiveRegionColumns.forEach((region) => {
        validKeys.add(toOverrideRowKey(region, row?.category));
      });
    });
    setDeepDiveEdits((current) => {
      const next = {};
      Object.entries(current || {}).forEach(([key, value]) => {
        if (validKeys.has(key)) next[key] = value;
      });
      return next;
    });
  }, [deepDivePivotRows, deepDiveRegionColumns]);
  const deepDiveEmptyColSpan = 6 + DEEP_DIVE_METRIC_GROUPS.reduce(
    (total, group) => total + Math.max(1, deepDiveRegionColumns.length) + (group.includeTotal ? 1 : 0),
    0
  );

  const syncAssessmentScopeOptions = useCallback((hierarchyPayload = {}, preferredL3 = "", preferredL4 = "") => {
    const nextHierarchy = {};
    Object.entries(hierarchyPayload || {}).forEach(([rawL3, rawL4Values]) => {
      const l3 = String(rawL3 || "").trim();
      if (!l3) return;
      const l4Values = Array.from(
        new Set(
          (Array.isArray(rawL4Values) ? rawL4Values : [])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right));
      nextHierarchy[l3] = l4Values;
    });

    const nextCategories = Object.keys(nextHierarchy).sort((left, right) => left.localeCompare(right));
    const resolvedL3 = [preferredL3, assessmentSelectedL3, nextCategories[0], ""]
      .map((value) => String(value || "").trim())
      .find((value) => value && nextCategories.includes(value)) || "";
    const resolvedL4Options = resolvedL3 ? (nextHierarchy[resolvedL3] || []) : [];
    const resolvedL4 = [preferredL4, assessmentSelectedL4]
      .map((value) => String(value || "").trim())
      .find((value) => value && resolvedL4Options.includes(value)) || "";

    setAssessmentCategoryHierarchy(nextHierarchy);
    setAssessmentCategories(nextCategories);
    setAssessmentSelectedL3(resolvedL3);
    setAssessmentSelectedL4(resolvedL4);
  }, [assessmentSelectedL3, assessmentSelectedL4]);

  const loadAssessmentCategories = useCallback(async ({ silent = false } = {}) => {
    if (!sessionId) return;

    setIsAssessmentCategoriesLoading(true);
    if (!silent) {
      setAssessmentError("");
    }

    try {
      const response = await axios.post(
        assessmentCategoriesUrl,
        { session_id: sessionId },
        { timeout: 180000 }
      );
      const data = normalizeApiPayload(response?.data);
      syncAssessmentScopeOptions(
        data?.category_hierarchy || {},
        data?.default_category_l1 || "",
        ""
      );
      setAssessmentError("");
    } catch (error) {
      const message = await resolveErrorMessage(error);
      const fallbackKeys = Object.keys(assessmentHierarchyFallback || {});
      if (fallbackKeys.length) {
        syncAssessmentScopeOptions(assessmentHierarchyFallback);
        setAssessmentError(`${message} Using dashboard-derived scope instead.`);
      } else {
        setAssessmentError(message);
      }
    } finally {
      setIsAssessmentCategoriesLoading(false);
    }
  }, [
    assessmentCategoriesUrl,
    assessmentHierarchyFallback,
    sessionId,
    syncAssessmentScopeOptions,
  ]);

  const loadAssessmentArtifacts = useCallback(async function loadArtifactsRequest(artifactsPath, attempt = 0) {
    if (!artifactsPath) return;

    const requestVersion = assessmentScopeVersionRef.current;
    setIsAssessmentArtifactsLoading(true);
    try {
      const response = await axios.get(buildApiUrl(apiBase, artifactsPath), { timeout: 300000 });
      if (requestVersion !== assessmentScopeVersionRef.current) {
        return;
      }
      const data = normalizeApiPayload(response?.data);
      setAssessmentArtifacts(data || null);
      setAssessmentError("");
    } catch (error) {
      if (requestVersion !== assessmentScopeVersionRef.current) {
        return;
      }
      let debugBody = error?.response?.data;
      if (debugBody instanceof Blob) {
        try {
          debugBody = await debugBody.text();
        } catch (_blobReadError) {
          debugBody = "[blob read failed]";
        }
      }
      console.error("[RAChemical][SpendAssessment] artifacts request failed", {
        artifactsPath,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: debugBody,
        headers: error?.response?.headers,
        message: error?.message,
      });
      const fallbackJobId = extractAssessmentJobId(artifactsPath);
      const fallbackPath = fallbackJobId ? `/rapidai/pipeline/artifacts/${fallbackJobId}` : "";
      if (
        fallbackPath &&
        fallbackPath !== artifactsPath &&
        error?.response?.status >= 500 &&
        attempt === 0
      ) {
        console.warn("[RAChemical][SpendAssessment] retrying against RapidAI pipeline artifacts", {
          artifactsPath,
          fallbackPath,
        });
        await loadArtifactsRequest(fallbackPath, attempt + 1);
        return;
      }
      if (error?.response?.status === 409 && attempt < 8) {
        console.warn("[RAChemical][SpendAssessment] artifacts not ready yet, retrying", {
          artifactsPath,
          attempt: attempt + 1,
        });
        setAssessmentError("");
        window.setTimeout(() => {
          loadArtifactsRequest(artifactsPath, attempt + 1);
        }, 3000);
        return;
      }
      const message = await resolveErrorMessage(error);
      setAssessmentError(message);
    } finally {
      if (requestVersion === assessmentScopeVersionRef.current) {
        setIsAssessmentArtifactsLoading(false);
      }
    }
  }, [apiBase]);

  const handleAssessmentArtifactDownload = useCallback(async (artifactType) => {
    const files = assessmentArtifacts?.files;
    if (!files || typeof files !== "object") {
      setAssessmentError("Assessment output files are not available yet.");
      return;
    }

    const candidates = artifactType === "word"
      ? ["word_report"]
      : ["initiative_summary_workbook", "proofpoints_workbook", "savings_summary_workbook"];
    const artifact = candidates
      .map((key) => files?.[key])
      .find((item) => item && typeof item === "object");

    if (!artifact?.data) {
      setAssessmentError(`${artifactType === "word" ? "Word" : "Excel"} report is not available for this run.`);
      return;
    }

    setAssessmentDownloadingArtifact(artifactType);
    setAssessmentError("");
    try {
      const blob = decodeBase64ToBlob(
        artifact?.data,
        artifact?.content_type || artifact?.contentType || "application/octet-stream"
      );
      const fallbackName = artifactType === "word"
        ? "RapidAI_Chemical_Spend_Assessment_Report.docx"
        : "RapidAI_Chemical_Spend_Assessment_Output.xlsx";
      triggerDownload(blob, artifact?.filename || fallbackName);
    } catch (error) {
      const message = error?.message || `Unable to download ${artifactType} report.`;
      setAssessmentError(message);
    } finally {
      setAssessmentDownloadingArtifact("");
    }
  }, [assessmentArtifacts]);

  const handleRunAssessment = useCallback(async () => {
    if (!sessionId) {
      setAssessmentError("Start a dashboard session before running spend assessment.");
      return;
    }
    if (!assessmentSelectedL3) {
      setAssessmentError("Select an L3 category before running spend assessment.");
      return;
    }

    clearAssessmentRunState();
    setIsAssessmentRunning(true);
    setAssessmentError("");

    try {
      const response = await axios.post(
        assessmentRunUrl,
        {
          session_id: sessionId,
          category_l1: assessmentSelectedL3,
          category_l2: assessmentSelectedL4 || "",
          generate_word_report: true,
        },
        { timeout: 300000 }
      );
      const data = normalizeApiPayload(response?.data);
      setAssessmentRunMeta(data || null);
      const queuedPayload = data?.job_id
        ? {
            status: data?.job_status || "queued",
            status_label: "Queued",
            current_step: "Queued",
            percent_complete: 1,
            current_activity: "Assessment request accepted. Waiting for the spend-assessment run to start.",
            steps: [],
          }
        : null;
      assessmentStatusRef.current = queuedPayload;
      setAssessmentStatusPayload(queuedPayload);
    } catch (error) {
      const message = await resolveErrorMessage(error);
      setAssessmentError(message);
    } finally {
      setIsAssessmentRunning(false);
    }
  }, [
    assessmentRunUrl,
    assessmentSelectedL3,
    assessmentSelectedL4,
    clearAssessmentRunState,
    sessionId,
  ]);

  useEffect(() => {
    if (!sessionId) {
      resetAssessmentState();
      return;
    }
    if (!assessmentCategories.length) {
      const fallbackKeys = Object.keys(assessmentHierarchyFallback || {});
      if (fallbackKeys.length) {
        syncAssessmentScopeOptions(assessmentHierarchyFallback);
      }
    }
  }, [
    assessmentCategories.length,
    assessmentHierarchyFallback,
    resetAssessmentState,
    sessionId,
    syncAssessmentScopeOptions,
  ]);

  useEffect(() => {
    const options = assessmentSelectedL3
      ? (assessmentCategoryHierarchy?.[assessmentSelectedL3] || [])
      : [];
    if (assessmentSelectedL4 && !options.includes(assessmentSelectedL4)) {
      setAssessmentSelectedL4("");
    }
  }, [assessmentCategoryHierarchy, assessmentSelectedL3, assessmentSelectedL4]);

  useEffect(() => {
    assessmentStatusRef.current = assessmentStatusPayload;
  }, [assessmentStatusPayload]);

  useEffect(() => {
    if (view !== "dashboard" || activeDashboardTab !== "assessment" || !sessionId) {
      return;
    }
    if (assessmentCategories.length || isAssessmentCategoriesLoading) {
      return;
    }
    loadAssessmentCategories({ silent: false });
  }, [
    activeDashboardTab,
    assessmentCategories.length,
    isAssessmentCategoriesLoading,
    loadAssessmentCategories,
    sessionId,
    view,
  ]);

  useEffect(() => {
    const jobId = String(assessmentRunMeta?.job_id || "").trim();
    const progressPath = String(
      assessmentRunMeta?.progress_url || assessmentRunMeta?.status_url || ""
    ).trim();
    if (!jobId || !progressPath) {
      return undefined;
    }

    const statusValue = String(assessmentStatusPayload?.status || "").trim().toLowerCase();
    if (["succeeded", "failed", "cancelled", "canceled"].includes(statusValue)) {
      return undefined;
    }

    let cancelled = false;

    const pollStatus = async () => {
      try {
        const response = await axios.get(buildApiUrl(apiBase, progressPath), { timeout: 180000 });
        if (cancelled) return;
        const data = normalizeApiPayload(response?.data);
        const isAccepted = shouldAcceptAssessmentStatus(assessmentStatusRef.current, data || null);
        if (isAccepted) {
          assessmentStatusRef.current = data || null;
          setAssessmentStatusPayload(data || null);
        }
        if (!isAccepted) return;

        const nextStatus = String(data?.status || "").trim().toLowerCase();
        if (nextStatus === "succeeded") {
          await loadAssessmentArtifacts(
            assessmentRunMeta?.artifacts_url || `/rapidai/pipeline/artifacts/${jobId}`
          );
        } else if (nextStatus === "failed") {
          setAssessmentError(data?.error || "Spend assessment failed.");
        }
      } catch (error) {
        if (cancelled) return;
        const message = await resolveErrorMessage(error);
        setAssessmentError(message);
      }
    };

    pollStatus();
    const intervalId = window.setInterval(pollStatus, Number(assessmentStatusPayload?.poll_after_ms || 2500));
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    apiBase,
    assessmentRunMeta,
    assessmentStatusPayload?.status,
    assessmentStatusPayload?.poll_after_ms,
    loadAssessmentArtifacts,
  ]);

  useEffect(() => {
    if (String(assessmentStatusPayload?.status || "").trim().toLowerCase() !== "succeeded") {
      return;
    }
    if (assessmentArtifacts || isAssessmentArtifactsLoading) {
      return;
    }
    const artifactsPath = String(
      assessmentRunMeta?.artifacts_url || assessmentStatusPayload?.artifacts_url || ""
    ).trim();
    if (!artifactsPath) {
      return;
    }
    loadAssessmentArtifacts(artifactsPath);
  }, [
    assessmentArtifacts,
    assessmentRunMeta,
    assessmentStatusPayload,
    isAssessmentArtifactsLoading,
    loadAssessmentArtifacts,
  ]);

  const getDeepDiveGroupCellClass = (groupIndex, cellIndex, totalCells, extraClass = "") => {
    const bandClass = groupIndex % 2 === 0 ? "is-band-even" : "is-band-odd";
    return [
      "ra-deep-dive-region-cell",
      bandClass,
      cellIndex === 0 ? "is-group-start" : "",
      cellIndex === totalCells - 1 ? "is-group-end" : "",
      extraClass,
    ].filter(Boolean).join(" ");
  };

  const handleDeepDiveEditChange = useCallback((row, field) => (event) => {
    const value = event.target.value;
    const rowKey =
      String(row?.row_key || "").trim() ||
      toOverrideRowKey(row?.Region || row?.region, row?.Category || row?.category);
    const region = String(row?.Region || row?.region || "").trim();
    const category = String(row?.Category || row?.category || "").trim();
    if (!rowKey || !region || !category) return;

    setDeepDiveEdits((current) => ({
      ...current,
      [rowKey]: {
        ...(current[rowKey] || {}),
        row_key: rowKey,
        region,
        category,
        [field]: value,
      },
    }));
  }, []);

  const handleResetDeepDiveEdits = useCallback(() => {
    setDeepDiveEdits({});
  }, []);

  const handleApplyDeepDiveEdits = useCallback(async () => {
    try {
      const nextControls = { ...controls };
      toDashboardPayload(sessionId || "local-session", nextControls, buildCategoryOverridePayload());
      setErrorMessage("");
      await refreshDashboard(nextControls, { silent: false });
    } catch (error) {
      setErrorMessage(error?.message || "Invalid deep dive overrides.");
    }
  }, [buildCategoryOverridePayload, controls, refreshDashboard, sessionId]);

  const handleL3CategoryToggle = useCallback((optionValue) => {
    const option = String(optionValue || "").trim();
    if (!option) return;

    if (option === "All") {
      setSelectedL3Categories(["All"]);
      return;
    }

    setSelectedL3Categories((current) => {
      const withoutAll = (Array.isArray(current) ? current : []).filter((item) => item !== "All");
      const exists = withoutAll.includes(option);
      const next = exists ? withoutAll.filter((item) => item !== option) : [...withoutAll, option];
      return next.length ? next : ["All"];
    });
  }, []);

  const handleSupplierToggle = useCallback((optionValue) => {
    const option = String(optionValue || "").trim();
    if (!option) return;

    if (option === "All") {
      setSelectedSuppliers(["All"]);
      return;
    }

    setSelectedSuppliers((current) => {
      const withoutAll = (Array.isArray(current) ? current : []).filter((item) => item !== "All");
      const exists = withoutAll.includes(option);
      const next = exists ? withoutAll.filter((item) => item !== option) : [...withoutAll, option];
      return next.length ? next : ["All"];
    });
  }, []);

  return (
    <div className="ra-chem-page">
      <div className="ra-chem-bg-grid" aria-hidden="true" />
      <div className="ra-chem-orb ra-chem-orb-a" aria-hidden="true" />
      <div className="ra-chem-orb ra-chem-orb-b" aria-hidden="true" />

      {showCategorization ? (
        <main className="ra-home-shell ra-categorization-shell">
          <div className="ra-home-topbar">
            <button type="button" className="ra-secondary-btn ra-home-topbar-btn" onClick={handleBackFromCategorization}>
              Back
            </button>
          </div>

          <p className="ra-pill">Rapid AI Chemicals</p>
          <h1>Initiate categorization</h1>
          <p className="ra-home-subtitle">
            Upload the spend workbook and molecular mapping workbook, trigger the Databricks job, and download the
            enriched outputs once the run completes.
          </p>

          <section className="ra-home-card">
            <div className="ra-home-card-header">
              <h2>Categorization workflow</h2>
              <span className="ra-connected">
                {categorizationStatus ? categorizationStatus.toUpperCase() : "Connected"}
              </span>
            </div>
            <p className="ra-home-card-subtitle">
              Files are uploaded to blob storage first, then the Databricks notebook job processes them and writes both
              outputs back to blob.
            </p>

            <label className="ra-upload-box">
              <span className="ra-upload-title">Spend sheet</span>
              <span className="ra-upload-meta">Excel only: .xlsx, .xls, .xlsm</span>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm"
                onChange={handleCategorizationSpendFileSelect}
                disabled={isCategorizationSubmitting}
              />
              <span className="ra-upload-file">
                {categorizationSpendFile ? categorizationSpendFile.name : "No file selected."}
              </span>
            </label>

            <label className="ra-upload-box">
              <span className="ra-upload-title">Molecular database</span>
              <span className="ra-upload-meta">Upload the molecular mapping workbook for enrichment.</span>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm"
                onChange={handleCategorizationMappingFileSelect}
                disabled={isCategorizationSubmitting}
              />
              <span className="ra-upload-file">
                {categorizationMappingFile ? categorizationMappingFile.name : "No file selected."}
              </span>
            </label>

            <button
              type="button"
              className="ra-primary-btn"
              onClick={handleStartCategorization}
              disabled={!categorizationSpendFile || !categorizationMappingFile || isCategorizationSubmitting}
            >
              {isCategorizationSubmitting ? "Triggering Databricks Job..." : "Run Categorization"}
            </button>

            {categorizationErrorMessage ? <div className="ra-message ra-error">{categorizationErrorMessage}</div> : null}
          </section>

          {categorizationRun ? (
            <section className="ra-home-card ra-categorization-results">
              <div className="ra-home-card-header">
                <h2>Run status</h2>
                <span className="ra-connected">
                  {isCategorizationPolling && ["queued", "running", "unknown"].includes(categorizationStatus)
                    ? "Refreshing"
                    : (categorizationStatus || "queued").toUpperCase()}
                </span>
              </div>

              <div className="ra-categorization-status-list">
                <div className="ra-categorization-status-row">
                  <span>Request ID</span>
                  <strong>{categorizationRequestId || "Pending"}</strong>
                </div>
                <div className="ra-categorization-status-row">
                  <span>Databricks Run ID</span>
                  <strong>{categorizationRun?.databricks?.run_id || "Pending"}</strong>
                </div>
                <div className="ra-categorization-status-row">
                  <span>Status</span>
                  <strong>{categorizationStatus || "queued"}</strong>
                </div>
                <div className="ra-categorization-status-row">
                  <span>Spend file</span>
                  <strong>{categorizationRun?.inputs?.spend?.filename || categorizationSpendFile?.name || "Pending"}</strong>
                </div>
                <div className="ra-categorization-status-row">
                  <span>Mapping file</span>
                  <strong>{categorizationRun?.inputs?.mapping?.filename || categorizationMappingFile?.name || "Pending"}</strong>
                </div>
              </div>

              {categorizationStatus === "succeeded" ? (
                <>
                  <div className="ra-categorization-tags">
                    <span>{`New L4 rows: ${Number(categorizationResult?.new_l4_rows || 0)}`}</span>
                    <span>{`Spend rows: ${Number(categorizationResult?.spend_rows || 0)}`}</span>
                    <span>{`Unique materials: ${Number(categorizationResult?.unique_materials || 0)}`}</span>
                  </div>
                  <div className="ra-categorization-actions">
                    <button
                      type="button"
                      className="ra-primary-btn"
                      onClick={() => handleCategorizationDownload("spend")}
                      disabled={categorizationDownloadingArtifact === "spend"}
                    >
                      {categorizationDownloadingArtifact === "spend" ? "Downloading..." : "Download Spend Output"}
                    </button>
                    <button
                      type="button"
                      className="ra-secondary-btn"
                      onClick={() => handleCategorizationDownload("mapping")}
                      disabled={categorizationDownloadingArtifact === "mapping"}
                    >
                      {categorizationDownloadingArtifact === "mapping" ? "Downloading..." : "Download Mapping Output"}
                    </button>
                  </div>
                </>
              ) : null}
            </section>
          ) : null}
        </main>
      ) : !showDashboard ? (
        <main className="ra-home-shell">
          <div className="ra-home-topbar">
            <span className="ra-home-topbar-spacer" />
            <button type="button" className="ra-secondary-btn ra-home-topbar-btn" onClick={handleOpenCategorization}>
              Initiate Categorization
            </button>
          </div>
          <p className="ra-pill">Rapid AI Chemicals</p>
          <h1>Generate savings insights in minutes</h1>
          <p className="ra-home-subtitle">
            Upload your spend dataset and proceed to a live savings dashboard powered by your data.
          </p>

          <section className="ra-home-card">
            <div className="ra-home-card-header">
              <h2>Workflow setup</h2>
              <span className="ra-connected">Connected</span>
            </div>
            <p className="ra-home-card-subtitle">Provide spend and pricing sheets once to launch the dashboard.</p>

            <label className="ra-upload-box">
              <span className="ra-upload-title">Spend sheet</span>
              <span className="ra-upload-meta">Excel or CSV supported. Include `Spend` and `Region` columns.</span>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                onChange={handleFileSelect}
                disabled={isInitializing}
              />
              <span className="ra-upload-file">{selectedSpendFile ? selectedSpendFile.name : "No file selected."}</span>
            </label>

            <label className="ra-upload-box">
              <span className="ra-upload-title">Pricing sheet</span>
              <span className="ra-upload-meta">Workbook with `L4 Category`, `Region`, and Jan-Dec month columns.</span>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                onChange={handlePricingFileSelect}
                disabled={isInitializing}
              />
              <span className="ra-upload-file">{selectedPricingFile ? selectedPricingFile.name : "No file selected."}</span>
            </label>

            <button
              type="button"
              className="ra-primary-btn"
              onClick={handleProceedToDashboard}
              disabled={!selectedSpendFile || !selectedPricingFile || isInitializing}
            >
              {isInitializing ? "Preparing Dashboard..." : "Proceed to Dashboard"}
            </button>

            {errorMessage ? <div className="ra-message ra-error">{errorMessage}</div> : null}
          </section>
        </main>
      ) : (
        <main className={`ra-dashboard-shell ${isSidePaneCollapsed ? "is-side-collapsed" : ""}`}>
          <header className="ra-dash-header">
            <div className="ra-dash-header-left">
              <p className="ra-pill">Rapid AI Chemical</p>
              <h1>{dashboardHeading}</h1>
            </div>
            <div className="ra-dash-header-right">
              <div className="ra-dash-actions">
                <button type="button" className="ra-secondary-btn" onClick={handleNewFile}>
                  New File
                </button>
                <button
                  type="button"
                  className="ra-primary-btn"
                  onClick={handleExport}
                  disabled={isExporting || isRefreshing}
                >
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              </div>
              <div className="ra-header-filters">
                {showPricingRangeControls ? (
                  <div className="ra-pricing-range-controls">
                    <label className="ra-header-region-picker ra-header-compact-picker">
                      <span>Lower Year</span>
                      <select
                        value={controls.pricingStartYear}
                        onChange={handlePricingRangeControlChange("pricingStartYear")}
                        disabled={isRefreshing}
                      >
                        {pricingYearOptions.map((year) => (
                          <option key={`start-year-${year}`} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="ra-header-region-picker ra-header-compact-picker">
                      <span>Upper Year</span>
                      <select
                        value={controls.pricingEndYear}
                        onChange={handlePricingRangeControlChange("pricingEndYear")}
                        disabled={isRefreshing}
                      >
                        {pricingYearOptions.map((year) => (
                          <option key={`end-year-${year}`} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="ra-header-region-picker ra-header-compact-picker">
                      <span>Lower Month</span>
                      <select
                        value={controls.pricingStartMonth}
                        onChange={handlePricingRangeControlChange("pricingStartMonth")}
                        disabled={isRefreshing || !pricingStartMonthOptions.length}
                      >
                        {pricingStartMonthOptions.length ? pricingStartMonthOptions.map((month) => (
                          <option key={`start-month-${month.month_index}`} value={month.month_index}>
                            {month.month_label}
                          </option>
                        )) : <option value="">N/A</option>}
                      </select>
                    </label>
                    <label className="ra-header-region-picker ra-header-compact-picker">
                      <span>Upper Month</span>
                      <select
                        value={controls.pricingEndMonth}
                        onChange={handlePricingRangeControlChange("pricingEndMonth")}
                        disabled={isRefreshing || !pricingEndMonthOptions.length}
                      >
                        {pricingEndMonthOptions.length ? pricingEndMonthOptions.map((month) => (
                          <option key={`end-month-${month.month_index}`} value={month.month_index}>
                            {month.month_label}
                          </option>
                        )) : <option value="">N/A</option>}
                      </select>
                    </label>
                  </div>
                ) : null}

              </div>
            </div>
          </header>

          <div className={`ra-dashboard-layout ${isSidePaneCollapsed ? "is-collapsed" : ""}`}>
            <aside className="ra-side-pane">
              <button
                type="button"
                className="ra-side-collapse-btn"
                onClick={() => setIsSidePaneCollapsed((current) => !current)}
                aria-label={isSidePaneCollapsed ? "Expand navigation" : "Collapse navigation"}
                title={isSidePaneCollapsed ? "Expand navigation" : "Collapse navigation"}
              >
                {isSidePaneCollapsed ? "»" : "«"}
              </button>
              <div className="ra-side-nav">
                <button
                  type="button"
                  className={`ra-side-nav-btn ${activeDashboardTab === "overview" ? "is-active" : ""}`}
                  onClick={() => setActiveDashboardTab("overview")}
                >
                  {!isSidePaneCollapsed ? "Spend Overview" : "Overview"}
                </button>
                <button
                  type="button"
                  className={`ra-side-nav-btn ${activeDashboardTab === "deepdive" ? "is-active" : ""}`}
                  onClick={() => setActiveDashboardTab("deepdive")}
                >
                  {!isSidePaneCollapsed ? "Category Input" : "Input"}
                </button>
                <button
                  type="button"
                  className={`ra-side-nav-btn ${activeDashboardTab === "assessment" ? "is-active" : ""}`}
                  onClick={() => setActiveDashboardTab("assessment")}
                >
                  {!isSidePaneCollapsed ? "Spend Assessment" : "Assessment"}
                </button>
              </div>
            </aside>

            <section className="ra-dashboard-content">
              {errorMessage ? <div className="ra-message ra-error">{errorMessage}</div> : null}

              {activeDashboardTab === "overview" ? (
            <>
          <section className="ra-kpi-showcase">
            <article className="ra-kpi-feature-card">
              <div className="ra-kpi-feature-head">
                <p>Total Spend</p>
                <span className="ra-kpi-badge">TS</span>
              </div>
              <h3>{formatCurrency(summary.total_spend)}</h3>
              <span>Across uploaded dataset</span>
              <div className="ra-kpi-divider" />
              <div className="ra-kpi-feature-meta">
                <div>
                  <p>Suppliers</p>
                  <h4>{Number(summary.suppliers_count || 0)}</h4>
                </div>
                <div>
                  <p>Categories</p>
                  <h4>{Number(summary.categories_count || 0)}</h4>
                </div>
                <div>
                  <p>Regions</p>
                  <h4>{Number(summary.regions_count || 0)}</h4>
                </div>
              </div>
            </article>

            <article className="ra-kpi-feature-card">
              <div className="ra-kpi-feature-head">
                <p>Addressable Spend</p>
                <span className="ra-kpi-badge">AS</span>
              </div>
              <h3>{formatCurrency(summary.total_addressable_spend)}</h3>
              <span>Based on addressibility factor</span>
              <div className="ra-kpi-divider" />
              <div className="ra-kpi-feature-meta">
                <div>
                  <p>Final Savings Low</p>
                  <h4>{formatPct(summary.final_savings_low_pct)}</h4>
                </div>
                <div>
                  <p>Final Savings High</p>
                  <h4>{formatPct(summary.final_savings_high_pct)}</h4>
                </div>
                <div>
                  <p>Avg Savings</p>
                  <h4>{formatPct(summary.avg_savings_pct)}</h4>
                </div>
              </div>
            </article>

            <article className="ra-kpi-feature-card">
              <div className="ra-kpi-feature-head">
                <p>Savings Potential</p>
                <span className="ra-kpi-badge">SP</span>
              </div>
              <h3>{formatCurrency(summary.potential_savings_high)}</h3>
              <span>{`High estimate at ${formatPct(summary.final_savings_high_pct)} savings rate`}</span>
              <div className="ra-kpi-divider" />
              <div className="ra-kpi-feature-meta">
                <div>
                  <p>Low Estimate</p>
                  <h4>{formatCurrency(summary.potential_savings_low)}</h4>
                </div>
                <div>
                  <p>Avg Estimate</p>
                  <h4>{formatCurrency(potentialSavingsAvg)}</h4>
                </div>
                <div>
                  <p>Savings Rate</p>
                  <h4>{formatPct(summary.avg_savings_pct)}</h4>
                </div>
              </div>
            </article>
          </section>

          <section className="ra-panel ra-overview-l4-panel">
            <div className="ra-panel-head">
              <div>
                <p className="ra-panel-kicker">L4 Spend Breakdown</p>
                <h2>L4 Category-wise Spend Breakdown</h2>
              </div>
              <div className="ra-value-chain-tools ra-category-tools">
                <label className="ra-chart-metric-picker">
                  <span>View Metric</span>
                  <select value={l4CategoryMetric} onChange={(event) => setL4CategoryMetric(event.target.value)}>
                    {CATEGORY_METRIC_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ra-chart-metric-picker">
                  <span>Category</span>
                  <select value={selectedCategory} onChange={(event) => setSelectedCategory(event.target.value)}>
                    {categoryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All categories" : option}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="ra-tags">
                  {isAllCategoriesSelected ? (
                    <>
                      <span>{`Top 80% ${categoryTopCount} categories`}</span>
                      <span>{`Tail ${categoryTailCount} categories`}</span>
                    </>
                  ) : (
                    <>
                      <span>{`Showing: ${selectedCategory}`}</span>
                      <span>{`${categoryChartData.length} category(s)`}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="ra-chart-wrap ra-chart-wrap-scroll">
              <div className="ra-chart-scroll">
                <BarChart width={categoryChartWidth} height={340} data={categoryChartData} margin={{ top: 10, right: 24, left: 4, bottom: 84 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="name" angle={-32} textAnchor="end" interval={0} height={98} tick={AXIS_TICK_STYLE}>
                      <Label value="L4 Category" position="insideBottom" offset={-32} style={AXIS_LABEL_STYLE} />
                    </XAxis>
                  <YAxis
                    width={90}
                    tick={AXIS_TICK_STYLE}
                    tickFormatter={selectedL4CategoryMetric.type === "percent" ? formatPct : formatCurrency}
                  >
                    <Label value={selectedL4CategoryMetric.displayLabel} angle={-90} position="insideLeft" style={AXIS_LABEL_STYLE} />
                  </YAxis>
                  <Tooltip
                    cursor={false}
                    formatter={(value) => {
                      if (selectedL4CategoryMetric.type === "percent") {
                        return [formatPct(value), selectedL4CategoryMetric.displayLabel];
                      }
                      return [formatCurrency(value), selectedL4CategoryMetric.displayLabel];
                    }}
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    itemStyle={TOOLTIP_ITEM_STYLE}
                  />
                  <Bar dataKey={selectedL4CategoryMetric.value} radius={[9, 9, 0, 0]}>
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </div>
            </div>
          </section>

          <section className="ra-panel ra-overview-l3-panel">
            <div className="ra-panel-head">
              <div>
                <p className="ra-panel-kicker">L3 Spend Breakdown</p>
                <h2>L3 Category-wise Spend Breakdown</h2>
              </div>
              <div className="ra-value-chain-tools ra-category-tools">
                <label className="ra-chart-metric-picker">
                  <span>View Metric</span>
                  <select value={l3CategoryMetric} onChange={(event) => setL3CategoryMetric(event.target.value)}>
                    {CATEGORY_METRIC_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="ra-chart-metric-picker ra-chart-metric-picker-multi" ref={l3DropdownRef}>
                  <span>L3 Categories (Multi-select)</span>
                  <button
                    type="button"
                    className="ra-multi-select-trigger"
                    onClick={() => setIsL3DropdownOpen((current) => !current)}
                    aria-expanded={isL3DropdownOpen ? "true" : "false"}
                  >
                    <span>{l3CategorySelectedLabel || "All L3 categories"}</span>
                    <span aria-hidden="true">{isL3DropdownOpen ? "▴" : "▾"}</span>
                  </button>
                  {isL3DropdownOpen ? (
                    <div className="ra-multi-select-menu">
                      {l3CategoryOptions.map((option) => {
                        const checked = option === "All"
                          ? isAllL3CategoriesSelected
                          : selectedL3Categories.includes(option);
                        return (
                          <label key={option} className="ra-multi-select-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleL3CategoryToggle(option)}
                            />
                            <span>{option === "All" ? "All L3 categories" : option}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
                <div className="ra-tags">
                  {isAllL3CategoriesSelected ? (
                    <>
                      <span>{`Top 80% ${l3CategoryTopCount} categories`}</span>
                      <span>{`Tail ${l3CategoryTailCount} categories`}</span>
                    </>
                  ) : (
                    <>
                      <span>{`Selected: ${selectedL3Categories.length}`}</span>
                      <span>{`Showing: ${l3CategoryChartData.length} category(s)`}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={`ra-chart-wrap ra-chart-wrap-l3 ${l3ChartShouldScroll ? "ra-chart-wrap-scroll" : ""}`}>
              {l3ChartShouldScroll ? (
                <div className="ra-chart-scroll">
                  <BarChart width={l3CategoryChartWidth} height={390} data={l3CategoryChartData} margin={{ top: 10, right: 24, left: 4, bottom: 84 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="name" angle={-32} textAnchor="end" interval={0} height={98} tick={AXIS_TICK_STYLE}>
                      <Label value="L3 Category" position="insideBottom" offset={-32} style={AXIS_LABEL_STYLE} />
                    </XAxis>
                    <YAxis
                      width={90}
                      tick={AXIS_TICK_STYLE}
                      tickFormatter={selectedL3CategoryMetric.type === "percent" ? formatPct : formatCurrency}
                    >
                      <Label value={selectedL3CategoryMetric.displayLabel} angle={-90} position="insideLeft" style={AXIS_LABEL_STYLE} />
                    </YAxis>
                    <Tooltip
                      cursor={false}
                      formatter={(value) => {
                        if (selectedL3CategoryMetric.type === "percent") {
                          return [formatPct(value), selectedL3CategoryMetric.displayLabel];
                        }
                        return [formatCurrency(value), selectedL3CategoryMetric.displayLabel];
                      }}
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                    />
                    <Bar dataKey={selectedL3CategoryMetric.value} radius={[9, 9, 0, 0]}>
                      {l3CategoryChartData.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={430}>
                  <BarChart data={l3CategoryChartData} margin={{ top: 10, right: 24, left: 4, bottom: 84 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="name" angle={-32} textAnchor="end" interval={0} height={98} tick={AXIS_TICK_STYLE}>
                      <Label value="L3 Category" position="insideBottom" offset={-32} style={AXIS_LABEL_STYLE} />
                    </XAxis>
                    <YAxis
                      width={90}
                      tick={AXIS_TICK_STYLE}
                      tickFormatter={selectedL3CategoryMetric.type === "percent" ? formatPct : formatCurrency}
                    >
                      <Label value={selectedL3CategoryMetric.displayLabel} angle={-90} position="insideLeft" style={AXIS_LABEL_STYLE} />
                    </YAxis>
                    <Tooltip
                      cursor={false}
                      formatter={(value) => {
                        if (selectedL3CategoryMetric.type === "percent") {
                          return [formatPct(value), selectedL3CategoryMetric.displayLabel];
                        }
                        return [formatCurrency(value), selectedL3CategoryMetric.displayLabel];
                      }}
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                    />
                    <Bar dataKey={selectedL3CategoryMetric.value} radius={[9, 9, 0, 0]}>
                      {l3CategoryChartData.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          <section className="ra-panel ra-overview-value-chain-panel">
            <div className="ra-panel-head">
              <div>
                <p className="ra-panel-kicker">Value Chain</p>
                <h2>Breakdown By Value Chain</h2>
              </div>
              <div className="ra-value-chain-tools">
                <label className="ra-chart-metric-picker">
                  <span>View Metric</span>
                  <select value={valueChainMetric} onChange={(event) => setValueChainMetric(event.target.value)}>
                    {VALUE_CHAIN_METRIC_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ra-chart-metric-picker">
                  <span>Value Chain</span>
                  <select value={selectedValueChain} onChange={(event) => setSelectedValueChain(event.target.value)}>
                    {valueChainOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All value chains" : option}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="ra-tags">
                  {isAllValueChainsSelected ? (
                    <>
                      <span>{`Top 80% ${valueChainTopCount} value chains`}</span>
                      <span>{`Tail ${valueChainTailCount} value chains`}</span>
                    </>
                  ) : (
                    <>
                      <span>{`Showing: ${selectedValueChain}`}</span>
                      <span>{`${valueChainChartData.length} value chain(s)`}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="ra-chart-wrap">
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart
                  data={valueChainChartData}
                  margin={{ top: 14, right: 28, left: 4, bottom: 88 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                  <XAxis dataKey="name" angle={-32} textAnchor="end" interval={0} height={102} tick={AXIS_TICK_SMALL_STYLE}>
                    <Label value="Value Chain" position="insideBottom" offset={-34} style={AXIS_LABEL_STYLE} />
                  </XAxis>
                  <YAxis
                    yAxisId="metric"
                    width={92}
                    tick={AXIS_TICK_STYLE}
                    tickFormatter={selectedValueChainMetric.type === "percent" ? formatPct : formatCurrency}
                  >
                    <Label value={selectedValueChainMetric.displayLabel} angle={-90} position="insideLeft" style={AXIS_LABEL_STYLE} />
                  </YAxis>
                  <Tooltip
                    cursor={false}
                    formatter={(value) => {
                      if (selectedValueChainMetric.type === "percent") {
                        return [formatPct(value), selectedValueChainMetric.displayLabel];
                      }
                      return [formatCurrency(value), selectedValueChainMetric.displayLabel];
                    }}
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    itemStyle={TOOLTIP_ITEM_STYLE}
                  />
                  {selectedValueChainMetric.type === "percent" ? (
                    <Line
                      yAxisId="metric"
                      type="monotone"
                      dataKey={selectedValueChainMetric.value}
                      name={selectedValueChainMetric.displayLabel}
                      stroke={selectedValueChainMetric.color}
                      strokeWidth={2.8}
                      dot={{ r: 2, fill: selectedValueChainMetric.color }}
                      activeDot={{ r: 4, fill: selectedValueChainMetric.color }}
                    />
                  ) : (
                    <Bar
                      yAxisId="metric"
                      dataKey={selectedValueChainMetric.value}
                      name={selectedValueChainMetric.displayLabel}
                      fill={selectedValueChainMetric.color}
                      radius={[8, 8, 0, 0]}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="ra-lower-grid">
            <article className="ra-panel">
              <div className="ra-panel-head">
                <div>
                  <p className="ra-panel-kicker">Distribution</p>
                  <h2>Regional Distribution</h2>
                </div>
              </div>

              <div className="ra-donut-wrap">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={regionalRows}
                      dataKey="spend"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {regionalRows.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={REGION_COLORS[index % REGION_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, _name, item) => [formatCurrency(value), item?.payload?.name || "Region"]}
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="ra-legend-list">
                {regionalRows.map((row, index) => (
                  <div key={`${row.name}-${index}`} className="ra-legend-row">
                    <span className="ra-legend-name">
                      <span className="ra-legend-dot" style={{ background: REGION_COLORS[index % REGION_COLORS.length] }} />
                      {row.name}
                    </span>
                    <span>{formatPct(row.share)}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="ra-panel">
              <div className="ra-panel-head">
                <div>
                  <p className="ra-panel-kicker">Suppliers</p>
                  <h2>Supplier-wise Spend Breakdown</h2>
                </div>
                <div className="ra-value-chain-tools ra-supplier-tools">
                  <div className="ra-chart-metric-picker ra-chart-metric-picker-multi" ref={supplierDropdownRef}>
                    <span>Suppliers (Multi-select)</span>
                    <button
                      type="button"
                      className="ra-multi-select-trigger"
                      onClick={() => setIsSupplierDropdownOpen((current) => !current)}
                      aria-expanded={isSupplierDropdownOpen ? "true" : "false"}
                    >
                      <span>{supplierSelectedLabel || "All suppliers"}</span>
                      <span aria-hidden="true">{isSupplierDropdownOpen ? "▴" : "▾"}</span>
                    </button>
                    {isSupplierDropdownOpen ? (
                      <div className="ra-multi-select-menu">
                        {supplierOptions.map((option) => {
                          const checked = option === "All"
                            ? isAllSuppliersSelected
                            : selectedSuppliers.includes(option);
                          return (
                            <label key={option} className="ra-multi-select-item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleSupplierToggle(option)}
                              />
                              <span>{option === "All" ? "All suppliers" : option}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="ra-tags">
                    {isAllSuppliersSelected ? (
                      <>
                        <span>{`Top 80% ${supplierTopCount} suppliers`}</span>
                        <span>{`Tail ${supplierTailCount} suppliers`}</span>
                      </>
                    ) : (
                      <>
                        <span>{`Selected: ${selectedSuppliers.length}`}</span>
                        <span>{`Showing: ${supplierRowsFiltered.length} supplier(s)`}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="ra-supplier-controls">
                <span>{`Showing ${supplierRowsFiltered.length ? supplierSliceStart + 1 : 0}-${Math.min(supplierSliceEnd, supplierRowsFiltered.length)} of ${supplierRowsFiltered.length}`}</span>
                <div>
                  <label>
                    Per page
                    <select value={supplierPerPage} onChange={(event) => setSupplierPerPage(Number(event.target.value) || 50)}>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="ra-secondary-btn"
                    onClick={() => setSupplierPage((current) => Math.max(0, current - 1))}
                    disabled={safeSupplierPage <= 0}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="ra-secondary-btn"
                    onClick={() => setSupplierPage((current) => Math.min(totalSupplierPages - 1, current + 1))}
                    disabled={safeSupplierPage >= totalSupplierPages - 1}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="ra-chart-wrap">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={supplierPageRows} margin={{ top: 10, right: 20, left: 4, bottom: 65 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="name" angle={-38} textAnchor="end" interval={0} height={75} tick={AXIS_TICK_SMALL_STYLE}>
                      <Label value="Supplier" position="insideBottom" offset={-22} style={AXIS_LABEL_STYLE} />
                    </XAxis>
                    <YAxis tick={AXIS_TICK_STYLE} tickFormatter={formatCurrency}>
                      <Label value="Total Spend" angle={-90} position="insideLeft" style={AXIS_LABEL_STYLE} />
                    </YAxis>
                    <Tooltip
                      cursor={false}
                      formatter={(value) => [formatCurrency(value), "Spend"]}
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={TOOLTIP_LABEL_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                    />
                    <Bar dataKey="spend" fill="#b26bff" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>
            </>
          ) : activeDashboardTab === "assessment" ? (
            <SpendAssessmentPanel
              categoriesLoading={isAssessmentCategoriesLoading}
              categoriesError=""
              categories={assessmentCategories}
              categoryHierarchy={assessmentCategoryHierarchy}
              selectedL3={assessmentSelectedL3}
              selectedL4={assessmentSelectedL4}
              onSelectL3={handleAssessmentSelectL3}
              onSelectL4={handleAssessmentSelectL4}
              onRunAssessment={handleRunAssessment}
              isRunning={isAssessmentRunning}
              statusPayload={assessmentStatusPayload}
              artifacts={assessmentArtifacts}
              isArtifactsLoading={isAssessmentArtifactsLoading}
              error={assessmentError}
              canDownloadWord={Boolean(assessmentArtifacts?.files?.word_report?.data)}
              canDownloadExcel={Boolean(
                assessmentArtifacts?.files?.initiative_summary_workbook?.data ||
                assessmentArtifacts?.files?.proofpoints_workbook?.data ||
                assessmentArtifacts?.files?.savings_summary_workbook?.data
              )}
              downloadingArtifact={assessmentDownloadingArtifact}
              onDownloadWord={() => handleAssessmentArtifactDownload("word")}
              onDownloadExcel={() => handleAssessmentArtifactDownload("excel")}
            />
          ) : (
            <section className="ra-panel">
              <div className="ra-panel-head">
                <div>
                  <h2>Editable Category Inputs</h2>
                </div>
                <div className="ra-deep-dive-filters">
                  <label>
                    <span>Search By Type - L4 Category</span>
                    <input
                      type="text"
                      value={deepDiveCategorySearch}
                      onChange={(event) => setDeepDiveCategorySearch(event.target.value)}
                      placeholder="Type category name"
                    />
                  </label>
                  <label>
                    <span>Filter By value chain</span>
                    <input
                      type="text"
                      value={deepDiveValueChainSearch}
                      onChange={(event) => setDeepDiveValueChainSearch(event.target.value)}
                      placeholder="Type value chain"
                    />
                  </label>
                </div>
                <div className="ra-deep-dive-actions">
                  {/* <span>{`${deepDiveFilteredRows.length} / ${deepDiveRows.length} row(s)`}</span> */}
                  <button
                    type="button"
                    className="ra-secondary-btn"
                    onClick={handleResetDeepDiveEdits}
                    disabled={!deepDiveEditedCount || isRefreshing}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="ra-primary-btn ra-deep-dive-apply"
                    onClick={handleApplyDeepDiveEdits}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>

              <div className="ra-deep-dive-table-wrap">
                <table className="ra-deep-dive-table">
                  <thead>
                    <tr>
                      <th className="ra-deep-dive-static-col" rowSpan={2}>By Type - L4 Category</th>
                      <th className="ra-deep-dive-static-col" rowSpan={2}>L3 Category</th>
                      <th className="ra-deep-dive-static-col" rowSpan={2}>By value chain</th>
                      <th className="ra-deep-dive-static-col" rowSpan={2}>By Type</th>
                      <th className="ra-deep-dive-static-col" rowSpan={2}>Primary Feedstock</th>
                      <th className="ra-deep-dive-static-col ra-deep-dive-static-col-end" rowSpan={2}>Primary Starting Material</th>
                      {DEEP_DIVE_METRIC_GROUPS.map((group, groupIndex) => (
                        <th
                          key={`deep-dive-group-${group.key}`}
                          className={`ra-deep-dive-region-group ${groupIndex % 2 === 0 ? "is-band-even" : "is-band-odd"}`}
                          colSpan={deepDiveRegionColumns.length + (group.includeTotal ? 1 : 0)}
                          title={group.fullLabel}
                          aria-label={group.fullLabel}
                        >
                          {group.headerLabel}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {DEEP_DIVE_METRIC_GROUPS.map((group, groupIndex) => {
                        const groupCellCount = deepDiveRegionColumns.length + (group.includeTotal ? 1 : 0);
                        return (
                          <React.Fragment key={`deep-dive-group-metrics-${group.key}`}>
                            {deepDiveRegionColumns.map((region, cellIndex) => (
                              <th
                                key={`deep-dive-group-${group.key}-${region}`}
                                className={`ra-deep-dive-metric-col ${groupIndex % 2 === 0 ? "is-band-even" : "is-band-odd"} ${cellIndex === 0 ? "is-group-start" : ""} ${!group.includeTotal && cellIndex === deepDiveRegionColumns.length - 1 ? "is-group-end" : ""}`}
                              >
                                <span className="ra-deep-dive-metric-label" title={group.fullLabel}>
                                  {region}
                                </span>
                              </th>
                            ))}
                            {group.includeTotal ? (
                              <th
                                key={`deep-dive-group-${group.key}-total`}
                                className={`ra-deep-dive-metric-col ${groupIndex % 2 === 0 ? "is-band-even" : "is-band-odd"} ${groupCellCount - 1 === 0 ? "is-group-start" : ""} is-group-end is-group-total`}
                              >
                                <span className="ra-deep-dive-metric-label" title={`${group.fullLabel} Total`}>
                                  Total
                                </span>
                              </th>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {deepDiveFilteredRows.length ? deepDiveFilteredRows.map((row, index) => {
                      const category = row.category || "Uncategorized";
                      const l3Category = row.l3Category || "Unspecified";
                      const valueChain = row.valueChain || "Unspecified";
                      const byType = row.byType || "Other";
                      const primaryFeedstock = row.primaryFeedstock || "Unspecified";
                      const primaryStartingMaterial = row.primaryStartingMaterial || "Unspecified";
                      const regionViews = deepDiveRegionColumns.map((region) => {
                        const regionRow = buildDeepDiveRegionRow(row, region);
                        const rowKey =
                          String(regionRow?.row_key || "").trim() ||
                          toOverrideRowKey(region, category);
                        const edit = deepDiveEdits[rowKey] || {};
                        const preview = computeDeepDivePreview(regionRow, edit);
                        const addressibilityFactor =
                          edit.addressibility_factor_percentage ?? toInputNumberString(preview.addressibilityPct);
                        const topDownLow =
                          edit.top_down_savings_low_pct ?? toInputNumberString(preview.topDownLowPct);
                        const topDownHigh =
                          edit.top_down_savings_high_pct ?? toInputNumberString(preview.topDownHighPct);
                        const lastSourcedBucket =
                          edit.last_sourced_bucket ?? toBucketControl(regionRow.last_sourced_bucket || "med");
                        const procurementBucket =
                          edit.procurement_maturity_bucket ?? toMaturityControl(regionRow.procurement_maturity_bucket || "med");
                        const avgFinalSavings =
                          edit.avg_final_savings_pct ?? toInputNumberString(preview.avgFinalSavingsPct);
                        const rangeText = `${formatPctFixed(preview.finalLowPct)} - ${formatPctFixed(preview.finalHighPct)}`;

                        const l4PriceChangeRaw = regionRow.l4_category_price_change_pct;
                        const primaryStartingMaterialPriceChangeRaw = regionRow.primary_starting_material_price_change_pct;
                        const primaryFeedstockPriceChangeRaw = regionRow.primary_feedstock_price_change_pct;
                        const l4PriceChange = (
                          l4PriceChangeRaw === null || l4PriceChangeRaw === undefined || String(l4PriceChangeRaw).trim() === ""
                        ) ? NaN : Number(l4PriceChangeRaw);
                        const primaryStartingMaterialPriceChange = (
                          primaryStartingMaterialPriceChangeRaw === null
                          || primaryStartingMaterialPriceChangeRaw === undefined
                          || String(primaryStartingMaterialPriceChangeRaw).trim() === ""
                        ) ? NaN : Number(primaryStartingMaterialPriceChangeRaw);
                        const primaryFeedstockPriceChange = (
                          primaryFeedstockPriceChangeRaw === null
                          || primaryFeedstockPriceChangeRaw === undefined
                          || String(primaryFeedstockPriceChangeRaw).trim() === ""
                        ) ? NaN : Number(primaryFeedstockPriceChangeRaw);

                        return {
                          region,
                          regionRow,
                          rowKey,
                          preview,
                          addressibilityFactor,
                          topDownLow,
                          topDownHigh,
                          lastSourcedBucket,
                          procurementBucket,
                          avgFinalSavings,
                          rangeText,
                          l4PriceChangeText: Number.isFinite(l4PriceChange) ? formatPctFixed(l4PriceChange) : "N/A",
                          primaryStartingMaterialPriceChangeText: Number.isFinite(primaryStartingMaterialPriceChange)
                            ? formatPctFixed(primaryStartingMaterialPriceChange)
                            : "N/A",
                          primaryFeedstockPriceChangeText: Number.isFinite(primaryFeedstockPriceChange)
                            ? formatPctFixed(primaryFeedstockPriceChange)
                            : "N/A",
                        };
                      });
                      const totalRegionalSpend = regionViews.reduce(
                        (sum, view) => sum + Number(view?.regionRow?.regional_spend ?? 0),
                        0
                      );
                      const totalAddressableSpend = regionViews.reduce(
                        (sum, view) => sum + Number(view?.preview?.addressableSpend ?? 0),
                        0
                      );

                      return (
                        <tr key={`${row.row_key}-${index}`}>
                          <td>{category}</td>
                          <td>{l3Category}</td>
                          <td>{valueChain}</td>
                          <td>{byType}</td>
                          <td>{primaryFeedstock}</td>
                          <td className="ra-deep-dive-static-col-end">{primaryStartingMaterial}</td>
                          {DEEP_DIVE_METRIC_GROUPS.map((group, groupIndex) => {
                            const groupCellCount = regionViews.length + (group.includeTotal ? 1 : 0);
                            const renderTotalCell = (value) => (
                              group.includeTotal ? (
                                <td className={getDeepDiveGroupCellClass(groupIndex, regionViews.length, groupCellCount, "is-group-total")}>
                                  {value}
                                </td>
                              ) : null
                            );

                            if (group.key === "regional_spend") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      {formatCurrency(view.regionRow?.regional_spend ?? 0)}
                                    </td>
                                  ))}
                                  {renderTotalCell(formatCurrency(totalRegionalSpend))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "addressibility_factor_percentage") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={view.addressibilityFactor}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "addressibility_factor_percentage")}
                                      />
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "addressable_spend") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      {formatCurrency(view.preview.addressableSpend)}
                                    </td>
                                  ))}
                                  {renderTotalCell(formatCurrency(totalAddressableSpend))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "top_down_savings_low_pct") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Low"
                                        value={view.topDownLow}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "top_down_savings_low_pct")}
                                      />
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "top_down_savings_high_pct") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="High"
                                        value={view.topDownHigh}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "top_down_savings_high_pct")}
                                      />
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "last_sourced_bucket") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <select
                                        value={view.lastSourcedBucket}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "last_sourced_bucket")}
                                      >
                                        {BUCKET_OPTIONS.map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "procurement_maturity_bucket") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <select
                                        value={view.procurementBucket}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "procurement_maturity_bucket")}
                                      >
                                        {MATURITY_OPTIONS.map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "final_savings_range") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      {view.rangeText}
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "avg_final_savings_pct") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={view.avgFinalSavings}
                                        onChange={handleDeepDiveEditChange({ row_key: view.rowKey, region: view.region, category }, "avg_final_savings_pct")}
                                      />
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "l4_category_price_change_pct") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      {view.l4PriceChangeText}
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            if (group.key === "primary_starting_material_price_change_pct") {
                              return (
                                <React.Fragment key={`${row.row_key}-${group.key}`}>
                                  {regionViews.map((view, cellIndex) => (
                                    <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                      {view.primaryStartingMaterialPriceChangeText}
                                    </td>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            return (
                              <React.Fragment key={`${row.row_key}-${group.key}`}>
                                {regionViews.map((view, cellIndex) => (
                                  <td key={`${row.row_key}-${group.key}-${view.region}`} className={getDeepDiveGroupCellClass(groupIndex, cellIndex, groupCellCount)}>
                                    {view.primaryFeedstockPriceChangeText}
                                  </td>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={deepDiveEmptyColSpan}>No rows match the current category/value-chain filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
            </section>
          </div>

          {isControlsModalOpen ? (
            <div className="ra-modal-backdrop" onClick={handleCloseControlsModal}>
              <section
                className="ra-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ra-parameters-title"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="ra-modal-head">
                  <h2 id="ra-parameters-title">Edit Parameters</h2>
                  <button type="button" className="ra-secondary-btn" onClick={handleCloseControlsModal}>
                    Close
                  </button>
                </div>
                <p className="ra-modal-subtitle">Update assumptions and apply to refresh the dashboard analytics.</p>

                <div className="ra-modal-grid">
                  <label>
                    <span>Procurement Maturity</span>
                    <select value={draftControls.procurementMaturity} onChange={handleDraftControlChange("procurementMaturity")}>
                      {MATURITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Addressibility Factor (%)</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={draftControls.addressibilityFactor}
                      onChange={handleDraftControlChange("addressibilityFactor")}
                    />
                  </label>
                  <label>
                    <span>Benefit Potential (Months)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draftControls.lastSourcedMonths}
                      onChange={handleDraftControlChange("lastSourcedMonths")}
                    />
                  </label>
                </div>

                <div className="ra-modal-actions">
                  <button type="button" className="ra-secondary-btn" onClick={handleCloseControlsModal}>
                    Cancel
                  </button>
                  <button type="button" className="ra-primary-btn ra-modal-apply" onClick={handleApplyControls} disabled={isRefreshing}>
                    {isRefreshing ? "Applying..." : "Apply Parameters"}
                  </button>
                </div>
              </section>
            </div>
          ) : null}

          {/* <footer className="ra-dashboard-footnote">
            {isRefreshing ? "Refreshing dashboard with current factors..." : "Change any factor to refresh dashboard analytics."}
          </footer> */}
        </main>
      )}
    </div>
  );
}
